import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../App.css';
import SearchableSelect from './SearchableSelect';
import {
  determineMarketByDistance,
  evaluateStandardDeliveryFee,
  evaluateExtendedOvernightFee,
  MULTI_DAY_COMPLIMENTARY_NOTE,
} from '../pricing/markets';
import {
  PER_DIEM_SUPERVISOR_RATE,
  PER_DIEM_TEAM_MEMBER_RATE,
  calculatePerDiemTotal,
  formatPerDiemExplanation,
} from '../pricing/perDiem';
import { calculateDrivingDistance } from '../pricing/distance';
import {
  formatCurrencyInput,
  normalizeCurrencyOnBlur,
  parseCurrencyInput,
} from '../pricing/currencyInput';
import { formatDisplayDate, formatDisplayDateRange, formatDisplayInstant } from '../utils/displayDate';

// State to timezone mapping (for US states)
const STATE_TIMEZONES = {
  'AL': 'America/Chicago', 'AK': 'America/Anchorage', 'AZ': 'America/Phoenix', 'AR': 'America/Chicago',
  'CA': 'America/Los_Angeles', 'CO': 'America/Denver', 'CT': 'America/New_York', 'DE': 'America/New_York',
  'FL': 'America/New_York', 'GA': 'America/New_York', 'HI': 'Pacific/Honolulu', 'ID': 'America/Denver',
  'IL': 'America/Chicago', 'IN': 'America/Indiana/Indianapolis', 'IA': 'America/Chicago', 'KS': 'America/Chicago',
  'KY': 'America/New_York', 'LA': 'America/Chicago', 'ME': 'America/New_York', 'MD': 'America/New_York',
  'MA': 'America/New_York', 'MI': 'America/Detroit', 'MN': 'America/Chicago', 'MS': 'America/Chicago',
  'MO': 'America/Chicago', 'MT': 'America/Denver', 'NE': 'America/Chicago', 'NV': 'America/Los_Angeles',
  'NH': 'America/New_York', 'NJ': 'America/New_York', 'NM': 'America/Denver', 'NY': 'America/New_York',
  'NC': 'America/New_York', 'ND': 'America/Chicago', 'OH': 'America/New_York', 'OK': 'America/Chicago',
  'OR': 'America/Los_Angeles', 'PA': 'America/New_York', 'RI': 'America/New_York', 'SC': 'America/New_York',
  'SD': 'America/Chicago', 'TN': 'America/Chicago', 'TX': 'America/Chicago', 'UT': 'America/Denver',
  'VT': 'America/New_York', 'VA': 'America/New_York', 'WA': 'America/Los_Angeles', 'WV': 'America/New_York',
  'WI': 'America/Chicago', 'WY': 'America/Denver', 'DC': 'America/New_York'
};

// ---------------------------------------------------------------------------
// Canonical proposal math — mirror of calculateInvoiceAndPoints() in the
// Events Proposal App (Apps Script). Kept in sync so the Delivery Portal's
// auto-filled Invoice/Product totals match the proposal & QuickBooks invoice.
// ---------------------------------------------------------------------------

// Rental multiplier by duration in 24-hour periods (NOT calendar days).
const getRentalMultiplier = (duration) => {
  if (duration <= 1) return 1.0;
  if (duration === 2) return 1.1;
  if (duration === 3) return 1.2;
  if (duration === 4) return 1.3;
  if (duration === 5) return 1.4;
  if (duration === 6) return 1.5;
  if (duration >= 7 && duration <= 14) return 2.0;
  if (duration >= 15 && duration <= 21) return 3.0;
  if (duration >= 22 && duration <= 28) return 4.0;
  return 4.0;
};

const parseTimeToHoursMinutes = (timeStr) => {
  if (!timeStr || String(timeStr).trim() === '') return null;
  let str = String(timeStr).trim();
  str = str.replace(/\s+(CST|CDT|EST|EDT|MST|MDT|PST|PDT|CT|ET|MT|PT)\s*$/i, '').trim();
  const upper = str.toUpperCase();
  const isPM = upper.indexOf('PM') >= 0;
  const isAM = upper.indexOf('AM') >= 0;
  const numPart = str.replace(/\s*[AP]M\s*$/i, '').trim();
  const colon = numPart.indexOf(':');
  if (colon < 0) return null;
  let hours = parseInt(numPart.substring(0, colon), 10);
  const minutes = parseInt(numPart.substring(colon + 1), 10) || 0;
  if (isNaN(hours)) return null;
  if (isPM && hours !== 12) hours += 12;
  if (isAM && hours === 12) hours = 0;
  hours = Math.max(0, Math.min(23, hours));
  return { hours: hours, minutes: Math.max(0, Math.min(59, minutes)) };
};

const parseDateTimeFromDateAndTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const tm = parseTimeToHoursMinutes(timeStr);
  if (!tm) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), tm.hours, tm.minutes, 0);
};

/**
 * Compute the canonical breakdown for a proposal, mirroring
 * calculateInvoiceAndPoints() in the Events Proposal App.
 * Returns null if the proposal has no usable section data.
 *
 * - productTotal           = rentalTotal (extended product total − discount)
 * - invoicePreTaxExDelivery = rentalTotal + productCare + serviceFee + miscFees
 *   (this is the proposal's Point Allocation: post-discount, pre-tax, excludes delivery)
 */
const computeProposalCanonicalTotals = (p) => {
  if (!p) return null;

  let sections;
  try {
    sections = JSON.parse(p.sectionsJSON || '[]');
  } catch (e) {
    return null;
  }
  if (!Array.isArray(sections) || sections.length === 0) return null;

  // Consolidate duplicate sections by name; within a section dedupe products by
  // name+unit price (keep first quantity seen), honoring per-product price overrides.
  const sectionMap = {};
  sections.forEach(section => {
    const name = (section.name || '').trim() || '(unnamed)';
    if (!sectionMap[name]) sectionMap[name] = {};
    const productMap = sectionMap[name];
    if (section.products && Array.isArray(section.products)) {
      section.products.forEach(product => {
        const useOverride = product.priceOverrideEnabled === true || product.priceOverrideEnabled === 'true' ||
          String(product.priceOverrideEnabled || '').toLowerCase() === 'true';
        const overrideVal = product.priceOverride !== undefined && product.priceOverride !== null && product.priceOverride !== ''
          ? parseFloat(product.priceOverride) : NaN;
        const unitPrice = (useOverride && !isNaN(overrideVal))
          ? overrideVal
          : (parseFloat(product.price || product.Price) || 0);
        const qty = parseFloat(product.qty || product.quantity || product.Qty || product.Quantity) || 0;
        const productKey = (product.name || '').toString().trim() + '|' + unitPrice;
        if (productKey in productMap) return; // duplicate section/product — keep first qty
        productMap[productKey] = { qty: qty, price: unitPrice };
      });
    }
  });

  let baseProductTotal = 0;
  Object.keys(sectionMap).forEach(sectionName => {
    const productMap = sectionMap[sectionName];
    Object.keys(productMap).forEach(key => {
      const prod = productMap[key];
      baseProductTotal += Math.max(0, prod.qty) * Math.max(0, prod.price);
    });
  });

  // Rental multiplier: custom override → delivery/strike 24-hour periods → date-only fallback.
  let multiplier = 1.0;
  let durationInDays = null;
  if (p.customRentalMultiplier && String(p.customRentalMultiplier).trim() !== '') {
    const parsed = parseFloat(p.customRentalMultiplier);
    if (!isNaN(parsed) && parsed > 0) multiplier = parsed;
  } else if (p.deliveryTime && p.strikeTime && p.startDate && p.endDate) {
    const deliveryDateTime = parseDateTimeFromDateAndTime(p.startDate, p.deliveryTime);
    const strikeDateTime = parseDateTimeFromDateAndTime(p.endDate, p.strikeTime);
    if (deliveryDateTime && strikeDateTime && !isNaN(deliveryDateTime.getTime()) && !isNaN(strikeDateTime.getTime())) {
      const diffHours = (strikeDateTime.getTime() - deliveryDateTime.getTime()) / (1000 * 60 * 60);
      durationInDays = Math.max(1, Math.ceil(diffHours / 24));
      multiplier = getRentalMultiplier(durationInDays);
    }
  }
  if (durationInDays == null && p.startDate && p.endDate) {
    const start = new Date(p.startDate);
    const end = new Date(p.endDate);
    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
      const diffTime = end.getTime() - start.getTime();
      durationInDays = Math.max(1, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
      multiplier = getRentalMultiplier(durationInDays);
    }
  }
  if (!multiplier || multiplier <= 0) multiplier = 1.0;

  const extendedProductTotal = baseProductTotal * multiplier;

  // Discount: type from column AH (discountType), then TYPE: prefix, then legacy >100 = dollar.
  const discountValue = parseFloat(p.discount || p.discountValue || 0) || 0;
  let discountType = 'percentage';
  if (p.discountType && String(p.discountType).trim().toLowerCase() === 'dollar') {
    discountType = 'dollar';
  } else if (p.discountName && p.discountName.startsWith('TYPE:')) {
    const match = p.discountName.match(/^TYPE:(\w+)/);
    if (match) discountType = match[1].toLowerCase();
  } else if (discountValue > 100) {
    discountType = 'dollar';
  }
  // Discount basis is the base product total unless EXTDISC:1 / discountOnExtended is set.
  let discountOnExtended = p.discountOnExtended === true || p.discountOnExtended === 'true' ||
    String(p.discountOnExtended || '').toLowerCase() === 'true' || p.discountOnExtended === 1;
  if (!discountOnExtended && p.discountName && String(p.discountName).indexOf('EXTDISC:') >= 0) {
    const extMatch = String(p.discountName).match(/EXTDISC:([01])/);
    if (extMatch) discountOnExtended = extMatch[1] === '1';
  }
  const discountBasis = discountOnExtended ? extendedProductTotal : baseProductTotal;
  const discount = discountType === 'dollar'
    ? Math.abs(discountValue)
    : discountBasis * (Math.abs(discountValue) / 100);

  const rentalTotal = extendedProductTotal - discount;

  // Product Care: 10% of extended product total (waivable).
  let productCare = extendedProductTotal * 0.10;
  if (p.discountName && p.discountName.includes('WAIVE:PC')) productCare = 0;

  const delivery = parseFloat(p.deliveryFee || 0) || 0;

  // Service Fee: 5% of (extended product total + product care + delivery) (waivable).
  let serviceFee = (extendedProductTotal + productCare + delivery) * 0.05;
  if (p.discountName && p.discountName.includes('WAIVE:SF')) serviceFee = 0;

  let miscFeesTotal = 0;
  try {
    const miscFees = typeof p.miscFees === 'string' ? JSON.parse(p.miscFees) : (p.miscFees || []);
    if (Array.isArray(miscFees)) {
      miscFeesTotal = miscFees.reduce((sum, fee) => {
        if (fee.hasOwnProperty('checked')) {
          return fee.checked ? sum + (parseFloat(fee.amount) || 0) : sum;
        }
        return sum + (parseFloat(fee.amount) || 0);
      }, 0);
    }
  } catch (e) {
    // Ignore misc fees parsing errors
  }

  // Point Allocation = pre-tax subtotal excluding delivery (rental + PC + SF + misc).
  const invoicePreTaxExDelivery = rentalTotal + productCare + serviceFee + miscFeesTotal;
  // Subtotal line (pre-tax) = everything before tax, INCLUDING delivery.
  const subtotalPreTax = invoicePreTaxExDelivery + delivery;

  return {
    baseProductTotal,
    extendedProductTotal,
    rentalTotal,
    productCare,
    serviceFee,
    miscFeesTotal,
    delivery,
    productTotal: rentalTotal,
    invoicePreTaxExDelivery,
    subtotalPreTax
  };
};

// Extract state from address string
const extractStateFromAddress = (address) => {
  if (!address) return null;
  // Try to match state abbreviations (2 letters) or full state names
  const stateMatch = address.match(/\b([A-Z]{2})\b/);
  if (stateMatch) {
    return stateMatch[1];
  }
  // Fallback: try to match common state name patterns
  const stateNames = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
    'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
    'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
    'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD', 'Massachusetts': 'MA',
    'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO', 'Montana': 'MT',
    'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM',
    'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
    'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD',
    'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA',
    'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC'
  };
  for (const [name, code] of Object.entries(stateNames)) {
    if (address.includes(name)) {
      return code;
    }
  }
  return null;
};

// Get timezone from address
const getTimezoneFromAddress = (address) => {
  const state = extractStateFromAddress(address);
  if (state && STATE_TIMEZONES[state]) {
    return STATE_TIMEZONES[state];
  }
  // Default to Central Time (Nashville) if we can't determine
  return 'America/Chicago';
};

/** Round a money value to cents (avoids float artifacts like 19841.660000000003). */
const roundMoney = (value) => {
  const n =
    typeof value === 'number'
      ? value
      : parseFloat(String(value ?? '').replace(/[$,\s]/g, ''));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
};

/** Store-ready money string with exactly two decimal places. */
const toMoneyInputString = (value) => {
  const n = roundMoney(value);
  return n == null ? '' : n.toFixed(2);
};

/** Reuses Production Packets section-header classes (sans number). */
function CalcSectionHead({ title }) {
  return (
    <div className="pp-section-head">
      <h3 className="pp-section-title">{title}</h3>
    </div>
  );
}

function ChevronLeftIcon({ size = 15 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronDownIcon({ open = false, size = 16 }) {
  return (
    <svg
      className={`accordion-chevron${open ? ' open' : ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/** Compact event window for workspace context: "Oct 2–3, 2026", "Oct 30–Nov 2, 2026". */
function formatContextEventRange(startDate, endDate, parseToLocalDate) {
  return formatDisplayDateRange(parseToLocalDate(startDate), parseToLocalDate(endDate));
}

function Calculator({ onSaveCalculation, savedCalculations = [], proposalsApiUrl = '' }) {
  const [projectNumber, setProjectNumber] = useState('');
  const [projectVersion, setProjectVersion] = useState(1);
  const [clientName, setClientName] = useState('');
  const [invoiceTotal, setInvoiceTotal] = useState('');
  const [productTotal, setProductTotal] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [isOvernightRequired, setIsOvernightRequired] = useState('');
  const [numberOfNights, setNumberOfNights] = useState('');
  const [nightsManuallySet, setNightsManuallySet] = useState(false);
  /**
   * Inclusive load-in → strike days for complimentary eligibility.
   * Used only when project event dates are unavailable (e.g. New Project).
   * Never inferred from warehouse departure → return.
   */
  const [eventDaysInput, setEventDaysInput] = useState('1');
  const [invoiceTotalManuallySet, setInvoiceTotalManuallySet] = useState(false);
  const [productTotalManuallySet, setProductTotalManuallySet] = useState(false);
  const [departureDateManuallySet, setDepartureDateManuallySet] = useState(false);
  const [returnDateManuallySet, setReturnDateManuallySet] = useState(false);
  const [selectedProjectData, setSelectedProjectData] = useState(null);
  const [numberOfTeamMembers, setNumberOfTeamMembers] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [distance, setDistance] = useState(null);
  const [market, setMarket] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deliveryTimezone, setDeliveryTimezone] = useState('America/Chicago');
  const [availableProjects, setAvailableProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectVersions, setProjectVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [eventType, setEventType] = useState('Standard');
  // Current proposal "live" data (for comparison + Refresh Proposal Data)
  const [currentProposalData, setCurrentProposalData] = useState(null);
  const [proposalDataUnavailable, setProposalDataUnavailable] = useState(false);
  const [loadedProposalFingerprint, setLoadedProposalFingerprint] = useState('');
  const [proposalDriftWhileDrafting, setProposalDriftWhileDrafting] = useState(false);
  const [proposalRefreshedAt, setProposalRefreshedAt] = useState(null);
  const [historyDetailsOpen, setHistoryDetailsOpen] = useState(false);
  const [historyResults, setHistoryResults] = useState(null);
  const [priorInvoiceTotal, setPriorInvoiceTotal] = useState(null);
  const [priorProductTotal, setPriorProductTotal] = useState(null);
  const [workingQuoteReady, setWorkingQuoteReady] = useState(false);
  const detailsFormRef = useRef(null);
  const newQuoteFormRef = useRef(null);
  const autoRefreshedProjectRef = useRef('');

  // Program (multi-event) delivery fields
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [includedEventProjects, setIncludedEventProjects] = useState([]); // projectNumbers toggled on
  const [calcMode, setCalcMode] = useState('project'); // 'project' | 'program' — which calculation type the user is performing
  const [programResults, setProgramResults] = useState(null);
  const [programCalcLoading, setProgramCalcLoading] = useState(false);
  const [programError, setProgramError] = useState('');
  const [programDetailsOpen, setProgramDetailsOpen] = useState(false); // Calculation Details accordion (collapsed by default)
  const [programInputsDirty, setProgramInputsDirty] = useState(false); // inputs changed since the last successful program calc
  const [showProgramSticky, setShowProgramSticky] = useState(false); // compact sticky summary visibility
  const [savingProgram, setSavingProgram] = useState(false);
  // Persistent confirmation of the last saved program delivery: { version, at }.
  // Cleared when inputs change or a new program is selected so it never lingers as stale.
  const [programSaveInfo, setProgramSaveInfo] = useState(null);
  const [programHistoryOpen, setProgramHistoryOpen] = useState(false); // Saved Program Deliveries accordion (collapsed by default)

  // Non-Standard Delivery fields
  const [lodgingRooms, setLodgingRooms] = useState('');
  const [lodgingNights, setLodgingNights] = useState('');
  const [lodgingTotal, setLodgingTotal] = useState('');
  const [lodgingRoomsManuallySet, setLodgingRoomsManuallySet] = useState(false);
  const [lodgingNightsManuallySet, setLodgingNightsManuallySet] = useState(false);
  const [lodgingTotalManuallySet, setLodgingTotalManuallySet] = useState(false);
  const [gasTotal, setGasTotal] = useState('');
  const [flightsCount, setFlightsCount] = useState('');
  const [flightsTotal, setFlightsTotal] = useState('');
  const [perDiemSupervisors, setPerDiemSupervisors] = useState('');
  const [perDiemTeamMembers, setPerDiemTeamMembers] = useState('');
  const [perDiemTotal, setPerDiemTotal] = useState('');
  const [perDiemTotalManuallySet, setPerDiemTotalManuallySet] = useState(false);
  const [truckRentalRequired, setTruckRentalRequired] = useState('');
  const [truckRentalCount, setTruckRentalCount] = useState('');
  const [truckRentalTotal, setTruckRentalTotal] = useState('');
  const [otherExpenses, setOtherExpenses] = useState([{ name: '', total: '' }]);
  
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const programResultsRef = useRef(null); // scroll target for the program Results section
  const programHeroRef = useRef(null); // observed to toggle the sticky results summary

  /** Any date input as the shared display standard ("Dec 8, 2027") for this screen. */
  const formatDateDisplay = (dateInput) => {
    if (dateInput == null || dateInput === '') return '';
    if (dateInput instanceof Date) return formatDisplayDate(dateInput);
    const standard = formatDisplayDate(dateInput);
    if (standard) return standard;
    const d = new Date(dateInput);
    return isNaN(d.getTime()) ? '' : formatDisplayDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
  };

  const formatSavedDateLocal = (timestamp) => {
    if (!timestamp) return null;
    const d = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    if (!d || isNaN(d.getTime())) return null;
    return formatDisplayInstant(d);
  };

  /** Medium date for version headers / history: "Apr 10, 2026". */
  const formatCalculatedDate = (timestamp) => {
    if (!timestamp) return '';
    const d = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    if (!d || isNaN(d.getTime())) return '';
    return formatDisplayDate(d);
  };

  const getMaxSavedVersion = (calcs = projectVersions) =>
    (calcs || []).reduce((max, calc) => Math.max(max, parseInt(calc.version, 10) || 1), 0);

  const buildProposalFingerprint = (project) => {
    if (!project) return '';
    const inv = calculateInvoiceTotalFromProposal(project);
    const prod = calculateProductTotalFromProposal(project);
    return JSON.stringify({
      version: project.version || 1,
      inv: inv == null ? null : roundMoney(inv),
      prod: prod == null ? null : roundMoney(prod),
      start: project.startDate ? String(project.startDate).split('T')[0] : '',
      end: project.endDate ? String(project.endDate).split('T')[0] : '',
      city: String(project.city || '').trim().toLowerCase(),
      state: String(project.state || '').trim().toLowerCase(),
    });
  };

  const hydrateResultsFromSnapshot = (versionCalc) => {
    if (!versionCalc) {
      setHistoryResults(null);
      return;
    }
    const totalFee = roundMoney(versionCalc.totalFee) || 0;
    setHistoryResults({
      standardFee: versionCalc.standardFee === 'Waived' || versionCalc.standardFee === 'waived'
        ? 'waived'
        : (roundMoney(versionCalc.standardFee) ?? 0),
      extendedFee: roundMoney(versionCalc.extendedFee) || 0,
      totalFee,
      days: versionCalc.days || null,
      eventDays: versionCalc.eventDays != null ? versionCalc.eventDays : versionCalc.thresholdDays ?? null,
      thresholdDays: versionCalc.thresholdDays != null ? versionCalc.thresholdDays : versionCalc.eventDays ?? null,
      freeThresholdApplied: versionCalc.freeThresholdApplied != null
        ? roundMoney(versionCalc.freeThresholdApplied)
        : null,
      complimentaryBlockedByMultiDay: !!versionCalc.complimentaryBlockedByMultiDay,
      nights: versionCalc.nights || versionCalc.numberOfNights || 0,
      teamMembers: versionCalc.numberOfTeamMembers || 0,
      market: versionCalc.market || null,
      distance: versionCalc.distance != null && versionCalc.distance !== ''
        ? String(versionCalc.distance)
        : null,
      amountToThreshold: versionCalc.amountToThreshold !== '' && versionCalc.amountToThreshold != null
        ? roundMoney(versionCalc.amountToThreshold)
        : null,
      extendedFeeExplanation: versionCalc.extendedFeeExplanation || '',
      isHistorical: true,
      isNonStandard: String(versionCalc.eventType || '') === 'Non-Standard Delivery',
    });
  };

  /** Format date range for display: "Load-In: Dec 8, 2027 Strike: Dec 9, 2027" (or single date when same). */
  const formatDateRange = (startDate, endDate) => {
    const loadIn = formatDateDisplay(startDate);
    const strike = formatDateDisplay(endDate);
    if (!loadIn && !strike) return '';
    if (!strike) return `Load-In: ${loadIn}`;
    if (!loadIn) return `Strike: ${strike}`;
    if (loadIn === strike) return `Load-In: ${loadIn}`;
    return `Load-In: ${loadIn} Strike: ${strike}`;
  };

  /** Parse a date input (Date, ISO, or YYYY-MM-DD) to a local Date, or null. */
  const parseToLocalDate = (dateInput) => {
    if (dateInput == null || dateInput === '') return null;
    let d;
    if (dateInput instanceof Date) {
      d = dateInput;
    } else if (typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}/)) {
      const part = dateInput.split('T')[0];
      const [y, m, day] = part.split('-').map(Number);
      d = new Date(y, m - 1, day);
    } else {
      d = new Date(dateInput);
    }
    return d && !isNaN(d.getTime()) ? d : null;
  };

  /** Compact range in the shared standard: "Sep 13–16, 2026", "Sep 30–Oct 2, 2026", "Dec 30, 2026–Jan 2, 2027". */
  const formatDateRangeShort = (startDate, endDate) => {
    const s = parseToLocalDate(startDate);
    const e = parseToLocalDate(endDate);
    return formatDisplayDateRange(s, e);
  };

  /**
   * "Today • 2:47 PM" / "Aug 7 • 2:47 PM" (year added when different).
   * If the source has no time-of-day (midnight — i.e. a date-only value), we
   * omit the time rather than fabricating "12:00 AM".
   */
  const formatUpdatedDateTime = (d) => {
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
    let datePart;
    if (sameDay) {
      datePart = 'Today';
    } else {
      const opts = { month: 'short', day: 'numeric' };
      if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
      datePart = d.toLocaleDateString('en-US', opts);
    }
    const hasTimeOfDay = d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
    if (!hasTimeOfDay) return datePart;
    const time = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${datePart} • ${time}`;
  };

  /**
   * Surface last-updated proposal metadata that ALREADY exists on the record
   * (no backend changes). Returns { text, by } or null. Structured so a future
   * "stale proposal" check can compare this timestamp to the calc time.
   */
  const getProposalUpdated = (ev) => {
    const p = ev && ev.fullProposal ? ev.fullProposal : null;
    if (!p) return null;
    const rawTs = p.timestamp || p.lastModified || p.updatedAt || p.lastUpdatedISO || null;
    const d = parseToLocalDate(rawTs);
    let text = d ? formatUpdatedDateTime(d) : (typeof p.lastUpdated === 'string' && p.lastUpdated ? p.lastUpdated : null);
    if (!text) return null;
    const by = p.lastModifiedBy || p.modifiedBy || p.updatedBy || p.lastEditedBy || p.editedBy || null;
    return { text, by: by || null };
  };

  // Invoice Total for delivery % fee = pre-tax subtotal EXCLUDING existing delivery
  // (rental + product care + service fee + misc). Same as pointAllocation /
  // invoicePreTaxExDelivery — not the proposal Subtotal line (which includes delivery).
  const calculateInvoiceTotalFromProposal = (proposal) => {
    if (!proposal || !proposal.fullProposal) return null;

    try {
      const p = proposal.fullProposal;

      // If historical invoice total exists, use it
      if (p.isHistorical && p.historicalInvoiceTotal) {
        return parseFloat(p.historicalInvoiceTotal) || null;
      }

      // 1) Point Allocation is already pre-tax excl. delivery (preferred when present).
      const pointAllocation = roundMoney(p.pointAllocation);
      if (pointAllocation != null && pointAllocation > 0) {
        return pointAllocation;
      }

      // 2) Derive from proposal Subtotal line: subtotalPreTax − deliveryFee.
      const subtotalPreTax = roundMoney(p.subtotalPreTax);
      const delivery = roundMoney(p.deliveryFee) || 0;
      if (subtotalPreTax != null && subtotalPreTax > 0) {
        const exclDelivery = roundMoney(subtotalPreTax - delivery);
        if (exclDelivery != null && exclDelivery > 0) return exclDelivery;
      }

      // 3) Last-resort local fallback (older proposals without computed fields).
      const totals = computeProposalCanonicalTotals(p);
      if (!totals) return null;
      return totals.invoicePreTaxExDelivery > 0
        ? roundMoney(totals.invoicePreTaxExDelivery)
        : null;
    } catch (err) {
      console.error('Error calculating invoice total from proposal:', err);
      return null;
    }
  };

  // Product Total (post-discount) = the proposal's Rental Total (discount already applied).
  // PREFER the proposal app's computed rentalTotal instead of re-deriving the math.
  const calculateProductTotalFromProposal = (proposal) => {
    if (!proposal || !proposal.fullProposal) return null;

    try {
      const p = proposal.fullProposal;

      // 1) Authoritative value straight from the proposal app's own calculation.
      const authoritative = roundMoney(p.rentalTotal);
      if (authoritative != null && authoritative > 0) return authoritative;

      // 2) Last-resort local fallback (older proposals without computed fields).
      const totals = computeProposalCanonicalTotals(p);
      if (!totals) return null;
      return totals.productTotal > 0 ? roundMoney(totals.productTotal) : null;
    } catch (err) {
      console.error('Error calculating product total from proposal:', err);
      return null;
    }
  };

  // Load available projects from Event Proposal Systems
  useEffect(() => {
    if (proposalsApiUrl) {
      loadProjects();
    }
  }, [proposalsApiUrl]);

  // Normalize project number for comparison (sheet may store 258 as number, dropdown may use "258" as string)
  const projectNumberNorm = projectNumber ? String(projectNumber).trim() : '';

  // Group available projects into programs by shared Program ID (e.g. PRG-2026-001).
  // Each program lists its linked events (latest version per project).
  const programs = useMemo(() => {
    const byId = new Map();
    (availableProjects || []).forEach((p) => {
      const id = (p.programId || '').trim();
      if (!id) return;
      if (!byId.has(id)) {
        byId.set(id, {
          programId: id,
          programName: (p.programName || '').trim() || id,
          events: [],
        });
      }
      byId.get(id).events.push(p);
    });
    return Array.from(byId.values())
      .map((prog) => {
        const events = prog.events.slice().sort((a, b) => {
          const aDate = a.startDate ? new Date(a.startDate).getTime() : 0;
          const bDate = b.startDate ? new Date(b.startDate).getTime() : 0;
          return aDate - bDate;
        });
        // Client name (shared across the program); strip any "(V2)" version suffix.
        const rawClient = (events.find((e) => e.clientName) || {}).clientName || '';
        const clientName = String(rawClient).replace(/\s*\(V\d+\)\s*$/i, '').trim();
        // Program date range: earliest load-in → latest strike across its events.
        let minStart = null;
        let maxEnd = null;
        let rangeStart = '';
        let rangeEnd = '';
        events.forEach((e) => {
          if (e.startDate) {
            const s = new Date(e.startDate).getTime();
            if (!isNaN(s) && (minStart == null || s < minStart)) { minStart = s; rangeStart = e.startDate; }
          }
          if (e.endDate) {
            const en = new Date(e.endDate).getTime();
            if (!isNaN(en) && (maxEnd == null || en > maxEnd)) { maxEnd = en; rangeEnd = e.endDate; }
          }
        });
        return { ...prog, events, clientName, rangeStart, rangeEnd };
      })
      .sort((a, b) => {
        const ac = (a.clientName || a.programName || a.programId).toLowerCase();
        const bc = (b.clientName || b.programName || b.programId).toLowerCase();
        return ac.localeCompare(bc);
      });
  }, [availableProjects]);

  const selectedProgram = useMemo(
    () => programs.find((prog) => prog.programId === selectedProgramId) || null,
    [programs, selectedProgramId]
  );

  // When a program is selected, default to including all of its events.
  useEffect(() => {
    if (selectedProgram) {
      setIncludedEventProjects(selectedProgram.events.map((e) => e.projectNumber));
      setProgramResults(null);
      setProgramError('');
      setProgramInputsDirty(false);
      setShowProgramSticky(false);
      setProgramDetailsOpen(false);
      setProgramSaveInfo(null);
      setProgramHistoryOpen(false);
    } else {
      setIncludedEventProjects([]);
      setProgramResults(null);
      setProgramError('');
      setProgramInputsDirty(false);
      setShowProgramSticky(false);
      setProgramSaveInfo(null);
      setProgramHistoryOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgramId]);

  const toggleIncludedEvent = (projectNumber) => {
    // Keep the existing fee visible but flag it as stale (see the dirty-input effect).
    setIncludedEventProjects((prev) =>
      prev.includes(projectNumber)
        ? prev.filter((pn) => pn !== projectNumber)
        : [...prev, projectNumber]
    );
  };

  // After a successful program calculation, smoothly bring the Results into view.
  useEffect(() => {
    if (programResults && programResultsRef.current) {
      programResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [programResults]);

  // Show the compact sticky summary once the hero fee scrolls above the viewport,
  // and hide it again when the user scrolls back up to the Results section.
  useEffect(() => {
    const el = programHeroRef.current;
    if (!programResults || !el || typeof IntersectionObserver === 'undefined') {
      setShowProgramSticky(false);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowProgramSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [programResults]);

  // Flag the displayed program fee as stale whenever a pricing-relevant input changes.
  // Also clear any prior save confirmation — it no longer matches the current inputs.
  useEffect(() => {
    if (programResults) {
      setProgramInputsDirty(true);
      setProgramSaveInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includedEventProjects, isOvernightRequired, numberOfNights, numberOfTeamMembers, eventType, invoiceTotal, productTotal, departureDate, returnDate, deliveryAddress]);

  // Per-event itemization of the program totals (latest proposal version for each event),
  // shown beneath the combined Invoice/Product fields so the aggregate can be verified.
  const programItemization = useMemo(() => {
    if (!selectedProgram) return null;
    const included = selectedProgram.events.filter((e) =>
      includedEventProjects.includes(e.projectNumber)
    );
    const rows = included.map((e) => ({
      projectNumber: e.projectNumber,
      venueName: e.venueName,
      clientName: e.clientName,
      version: e.version || 1,
      invoice: calculateInvoiceTotalFromProposal(e) || 0,
      product: calculateProductTotalFromProposal(e) || 0,
    }));
    const sumInvoice = rows.reduce((s, r) => s + r.invoice, 0);
    const sumProduct = rows.reduce((s, r) => s + r.product, 0);
    return { rows, sumInvoice, sumProduct };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgram, includedEventProjects]);

  // Prior saved program delivery calculations for the selected program (most recent first).
  const programHistory = useMemo(() => {
    if (!selectedProgramId) return [];
    return savedCalculations
      .filter((c) => c && c.type === 'program' && String(c.programId) === String(selectedProgramId))
      .slice()
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [savedCalculations, selectedProgramId]);

  // Auto-populate the Project Details section from the selected program's included events,
  // mirroring how a single project fills in. Client name always fills; totals re-sum as the
  // user toggles which events are included; dates span the full program; the delivery
  // address uses the primary (earliest load-in) event.
  useEffect(() => {
    if (!selectedProgramId || !selectedProgram || !programItemization) return;

    setClientName(selectedProgram.clientName || '');
    setEventType('Standard');

    setInvoiceTotal(programItemization.sumInvoice ? programItemization.sumInvoice.toFixed(2) : '');
    setProductTotal(programItemization.sumProduct ? programItemization.sumProduct.toFixed(2) : '');

    const eventsForFill = selectedProgram.events.filter((e) =>
      includedEventProjects.includes(e.projectNumber)
    );
    let minStart = null; let minStartDate = '';
    let maxEnd = null; let maxEndDate = '';
    eventsForFill.forEach((e) => {
      if (e.startDate) {
        const s = new Date(e.startDate).getTime();
        if (!isNaN(s) && (minStart == null || s < minStart)) { minStart = s; minStartDate = e.startDate; }
      }
      if (e.endDate) {
        const en = new Date(e.endDate).getTime();
        if (!isNaN(en) && (maxEnd == null || en > maxEnd)) { maxEnd = en; maxEndDate = e.endDate; }
      }
    });
    if (minStartDate) setDepartureDate(minStartDate);
    if (maxEndDate) setReturnDate(maxEndDate);

    const primary = eventsForFill
      .slice()
      .sort((a, b) => {
        const as = a.startDate ? new Date(a.startDate).getTime() : Infinity;
        const bs = b.startDate ? new Date(b.startDate).getTime() : Infinity;
        return as - bs;
      })[0];
    if (primary && primary.city && primary.state) {
      setDeliveryAddress(`${primary.city}, ${primary.state}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgramId, selectedProgram, programItemization]);

  // A project or program has been chosen — the Project Details section expands only then.
  const hasSelection = !!projectNumber || !!selectedProgramId;

  // Compute one delivery fee for the whole program: sum selected events' Invoice/Product,
  // use load-in → strike for complimentary eligibility, apply overnight from team travel
  // nights once, and flag when selected events resolve to different market tiers.
  const handleCalculateProgram = async () => {
    setProgramError('');
    setProgramResults(null);
    if (!selectedProgram) return;
    const included = selectedProgram.events.filter((e) =>
      includedEventProjects.includes(e.projectNumber)
    );
    if (included.length === 0) {
      setProgramError('Select at least one event to include in the program delivery.');
      return;
    }

    // Program delivery requires the same travel details as a single project, since they
    // drive the overnight/extended rate (charged when the team is gone longer than the
    // market's standard nights). Complete the "Travel details" section below.
    if (!isOvernightRequired) {
      setProgramError('Complete the Travel details below: select whether an overnight stay is required.');
      return;
    }
    if (isOvernightRequired === 'yes' && (!numberOfNights || parseInt(numberOfNights) < 1)) {
      setProgramError('Complete the Travel details below: enter the number of nights the team is gone (departure → return).');
      return;
    }
    if (!numberOfTeamMembers || parseInt(numberOfTeamMembers) < 1) {
      setProgramError('Complete the Travel details below: enter the number of team members.');
      return;
    }

    setProgramCalcLoading(true);
    try {
      let sumInvoice = 0;
      let sumProduct = 0;
      const distanceCache = {};
      const perEvent = [];
      let earliestStart = null;
      let latestEnd = null;

      for (const ev of included) {
        const inv = calculateInvoiceTotalFromProposal(ev) || 0;
        const prod = calculateProductTotalFromProposal(ev) || 0;
        sumInvoice += inv;
        sumProduct += prod;

        const addr = ev.city && ev.state ? `${ev.city}, ${ev.state}` : '';
        let dist = null;
        let mkt = null;
        if (addr) {
          if (Object.prototype.hasOwnProperty.call(distanceCache, addr)) {
            dist = distanceCache[addr];
          } else {
            try {
              dist = await calculateDistance(addr);
            } catch (e) {
              dist = null;
            }
            distanceCache[addr] = dist;
          }
          if (dist != null) mkt = determineMarket(dist);
        }

        // Event window (load-in → strike) drives the free-delivery threshold day count.
        if (ev.startDate) {
          const s = new Date(ev.startDate).getTime();
          if (!isNaN(s) && (earliestStart == null || s < earliestStart)) earliestStart = s;
        }
        if (ev.endDate) {
          const e2 = new Date(ev.endDate).getTime();
          if (!isNaN(e2) && (latestEnd == null || e2 > latestEnd)) latestEnd = e2;
        }

        perEvent.push({
          projectNumber: ev.projectNumber,
          venueName: ev.venueName,
          clientName: ev.clientName,
          invoice: inv,
          product: prod,
          market: mkt ? mkt.name : null,
          distance: dist,
        });
      }

      const programMarket = (() => {
        const found = perEvent.find((e) => e.market);
        if (!found) return null;
        return determineMarket(found.distance);
      })();

      if (!programMarket) {
        setProgramError('Could not determine a market for the program (no valid event address/distance).');
        setProgramCalcLoading(false);
        return;
      }

      const distinctTiers = Array.from(new Set(perEvent.filter((e) => e.market).map((e) => e.market)));

      // Event days across the whole program (earliest load-in → latest strike).
      let eventDays = 1;
      if (earliestStart != null && latestEnd != null && latestEnd > earliestStart) {
        eventDays = Math.max(1, Math.ceil((latestEnd - earliestStart) / (1000 * 60 * 60 * 24)) + 1);
      }

      // Complimentary Delivery Threshold applies to single-day only (not multi-day programs).
      const feeEval = evaluateStandardDeliveryFee({
        invoice: sumInvoice,
        product: sumProduct,
        market: programMarket,
        eventDays,
      });
      const {
        standardFee,
        amountToThreshold,
        freeThresholdApplied,
        feeFromPercentage,
        feeBasis,
        complimentaryBlockedByMultiDay,
      } = feeEval;

      // Overnight/extended fee applied ONCE for the program.
      const nights = parseInt(numberOfNights) || 0;
      const teamMembers = parseInt(numberOfTeamMembers) || 0;
      const overnightRequired = isOvernightRequired === 'yes';
      const overnightEval = evaluateExtendedOvernightFee({
        market: programMarket,
        nights,
        teamMembers,
        overnightRequired,
      });
      const extendedFee = overnightEval.extendedFee;
      const extendedFeeExplanation = overnightEval.explanation
        ? `${overnightEval.explanation} = $${extendedFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : null;

      const totalFee = standardFee === 'waived' || standardFee === null
        ? extendedFee
        : (typeof standardFee === 'number' ? standardFee : 0) + extendedFee;

      // Recommended distribution of the program fee across events, proportional to each
      // event's value (product total). Falls back to an even split with no product basis.
      const basisTotal = perEvent.reduce((s, e) => s + (e.product || 0), 0);
      const feeToDistribute = typeof totalFee === 'number' ? totalFee : 0;
      let allocations = perEvent.map((e) => ({
        projectNumber: e.projectNumber,
        venueName: e.venueName,
        clientName: e.clientName,
        product: e.product,
        invoice: e.invoice,
        share: basisTotal > 0 ? (e.product || 0) / basisTotal : (perEvent.length ? 1 / perEvent.length : 0),
        amount: 0,
      }));
      if (feeToDistribute > 0 && allocations.length > 0) {
        allocations = allocations.map((a) => ({
          ...a,
          amount: Math.round(feeToDistribute * a.share * 100) / 100,
        }));
        // Reconcile rounding so the allocations sum exactly to the program fee.
        const allocSum = allocations.reduce((s, a) => s + a.amount, 0);
        const diff = Math.round((feeToDistribute - allocSum) * 100) / 100;
        if (diff !== 0) {
          const last = allocations.length - 1;
          allocations[last] = {
            ...allocations[last],
            amount: Math.round((allocations[last].amount + diff) * 100) / 100,
          };
        }
      }

      setProgramResults({
        programId: selectedProgram.programId,
        programName: selectedProgram.programName,
        includedCount: included.length,
        sumInvoice,
        sumProduct,
        market: programMarket.name,
        standardFee,
        extendedFee,
        totalFee,
        thresholdDays: eventDays,
        freeThresholdApplied,
        freeThresholdPerDay: programMarket.freeThreshold != null ? programMarket.freeThreshold : null,
        complimentaryBlockedByMultiDay,
        feePercentage: programMarket.feePercentage,
        minFee: programMarket.minFee,
        feeFromPercentage,
        feeBasis,
        amountToThreshold,
        extendedFeeExplanation,
        marketDiscrepancy: distinctTiers.length > 1 ? distinctTiers : null,
        perEvent,
        allocations,
        distributionBasis: basisTotal > 0 ? 'product value' : 'even split',
      });
      // Freshly calculated — clear the stale-inputs flag.
      setProgramInputsDirty(false);
    } catch (err) {
      console.error('Program delivery calculation error:', err);
      setProgramError('Error calculating program delivery. Please try again.');
    } finally {
      setProgramCalcLoading(false);
    }
  };

  // Save the calculated program delivery fee as a versioned history record. Reuses the
  // same persistence path as individual projects (localStorage + Google Sheets), tagged
  // with type: 'program' and a programData snapshot of the events + allocation.
  const handleSaveProgram = async () => {
    if (!programResults || !selectedProgram || !onSaveCalculation) return;
    setSavingProgram(true);
    setProgramError('');
    try {
      const r = programResults;
      const nextVersion = programHistory.length > 0
        ? Math.max(...programHistory.map((c) => parseInt(c.version) || 0)) + 1
        : 1;

      const includedEvents = (r.perEvent || []).map((e) => {
        const src = selectedProgram.events.find((ev) => ev.projectNumber === e.projectNumber);
        return {
          projectNumber: e.projectNumber,
          venueName: e.venueName,
          clientName: e.clientName,
          version: src ? (src.version || 1) : null,
          invoice: e.invoice,
          product: e.product,
          market: e.market,
        };
      });

      const record = {
        timestamp: new Date().toISOString(),
        type: 'program',
        programId: selectedProgram.programId,
        programName: selectedProgram.programName || selectedProgram.clientName || '',
        projectNumber: '', // program-level record — not tied to a single project
        version: nextVersion,
        clientName: clientName || selectedProgram.clientName || '',
        eventType: 'Standard',
        invoiceTotal: r.sumInvoice || 0,
        productTotal: r.sumProduct || 0,
        deliveryAddress: deliveryAddress || '',
        departureDate: departureDate || '',
        returnDate: returnDate || '',
        isOvernightRequired: isOvernightRequired || '',
        numberOfNights: parseInt(numberOfNights) || 0,
        numberOfTeamMembers: parseInt(numberOfTeamMembers) || 0,
        distance: null,
        market: r.market || '',
        standardFee: r.standardFee === 'waived' ? 'waived' : (typeof r.standardFee === 'number' ? r.standardFee : (r.standardFee === null ? '' : 0)),
        extendedFee: r.extendedFee || 0,
        totalFee: typeof r.totalFee === 'number' ? r.totalFee : 0,
        days: r.thresholdDays || '',
        nights: parseInt(numberOfNights) || 0,
        amountToThreshold: r.amountToThreshold != null ? r.amountToThreshold : null,
        extendedFeeExplanation: r.extendedFeeExplanation || '',
        programData: {
          includedCount: r.includedCount,
          market: r.market,
          marketDiscrepancy: r.marketDiscrepancy || null,
          sumInvoice: r.sumInvoice,
          sumProduct: r.sumProduct,
          standardFee: r.standardFee,
          extendedFee: r.extendedFee,
          totalFee: r.totalFee,
          thresholdDays: r.thresholdDays,
          freeThresholdApplied: r.freeThresholdApplied,
          freeThresholdPerDay: r.freeThresholdPerDay,
          feePercentage: r.feePercentage,
          minFee: r.minFee,
          feeFromPercentage: r.feeFromPercentage,
          feeBasis: r.feeBasis,
          distributionBasis: r.distributionBasis,
          includedEvents,
          allocations: (r.allocations || []).map((a) => ({
            projectNumber: a.projectNumber,
            venueName: a.venueName,
            clientName: a.clientName,
            product: a.product,
            invoice: a.invoice,
            share: a.share,
            amount: a.amount,
          })),
        },
      };

      await onSaveCalculation(record);
      setProgramSaveInfo({ version: nextVersion, at: Date.now() });
    } catch (err) {
      console.error('Program save error:', err);
      setProgramError('Failed to save program delivery. Please try again.');
    } finally {
      setSavingProgram(false);
    }
  };

  // Sync Quote History for the selected project. Working form stays independent.
  useEffect(() => {
    if (!projectNumberNorm) {
      setProjectVersions([]);
      setProjectVersion(1);
      setSelectedVersion(null);
      setHistoryDetailsOpen(false);
      setHistoryResults(null);
      setWorkingQuoteReady(false);
      setProposalRefreshedAt(null);
      setPriorInvoiceTotal(null);
      setPriorProductTotal(null);
      autoRefreshedProjectRef.current = '';
      return;
    }

    const projectCalcs = savedCalculations
      .filter((calc) => calc.projectNumber != null && String(calc.projectNumber).trim() === projectNumberNorm)
      .filter((calc) => String(calc.type || 'project').toLowerCase() !== 'program')
      .sort((a, b) => (parseInt(b.version, 10) || 1) - (parseInt(a.version, 10) || 1));

    setProjectVersions(projectCalcs);
    const maxVersion = projectCalcs.reduce(
      (max, calc) => Math.max(max, parseInt(calc.version, 10) || 1),
      0
    );
    setProjectVersion(maxVersion + 1);

    if (projectCalcs.length === 0) {
      setSelectedVersion(null);
      setHistoryDetailsOpen(false);
      setHistoryResults(null);
      return;
    }

    setSelectedVersion((prev) => {
      if (prev != null && projectCalcs.some((c) => (parseInt(c.version, 10) || 1) === prev)) {
        return prev;
      }
      return parseInt(projectCalcs[0].version, 10) || 1;
    });
  }, [projectNumberNorm, savedCalculations]);

  // Keep historical snapshot results in sync with the selected Quote History item.
  useEffect(() => {
    if (selectedVersion == null || projectVersions.length === 0) {
      setHistoryResults(null);
      return;
    }
    const versionCalc = projectVersions.find(
      (v) => (parseInt(v.version, 10) || 1) === selectedVersion
    );
    hydrateResultsFromSnapshot(versionCalc || null);
  }, [selectedVersion, projectVersions]);

  // Detect proposal drift against the last intentional refresh.
  useEffect(() => {
    if (!workingQuoteReady || !selectedProjectData || !loadedProposalFingerprint) {
      setProposalDriftWhileDrafting(false);
      return;
    }
    const latest = buildProposalFingerprint(selectedProjectData);
    setProposalDriftWhileDrafting(latest !== loadedProposalFingerprint);
  }, [selectedProjectData, currentProposalData, loadedProposalFingerprint, workingQuoteReady]);

  // Re-fetch proposals on window focus so drift can surface while preparing a quote.
  useEffect(() => {
    if (!workingQuoteReady || !projectNumberNorm) return undefined;
    const projectKey = String(projectNumberNorm).trim();
    const onFocus = async () => {
      const projects = await loadProjects();
      const fresh = (projects || []).find(
        (p) => String(p.projectNumber).trim() === projectKey
      );
      if (fresh) setSelectedProjectData(fresh);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [workingQuoteReady, projectNumberNorm]);

  // Derive current proposal data for comparison / drift detection
  useEffect(() => {
    if (!selectedProjectData) {
      setCurrentProposalData(null);
      setProposalDataUnavailable(false);
      return;
    }
    const inv = calculateInvoiceTotalFromProposal(selectedProjectData);
    const prod = calculateProductTotalFromProposal(selectedProjectData);
    const start = selectedProjectData.startDate ? String(selectedProjectData.startDate).split('T')[0] : null;
    const end = selectedProjectData.endDate ? String(selectedProjectData.endDate).split('T')[0] : null;
    if (inv == null && prod == null) {
      setProposalDataUnavailable(true);
      setCurrentProposalData(null);
    } else {
      setProposalDataUnavailable(false);
      setCurrentProposalData({
        invoiceTotal: inv,
        productTotal: prod,
        startDate: start,
        endDate: end
      });
    }
  }, [selectedProjectData]);

  const normalizeDateForCompare = (d) => {
    if (d == null || d === '') return '';
    const s = String(d);
    if (s.match(/^\d{4}-\d{2}-\d{2}$/)) return s;
    if (s.includes('T')) return s.split('T')[0];
    const parsed = new Date(s);
    if (isNaN(parsed.getTime())) return '';
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const normalizeNumForCompare = (n) => {
    if (n == null || n === '') return null;
    const parsed = parseFloat(String(n).replace(/[$,]/g, ''));
    return Number.isNaN(parsed) ? null : Math.round(parsed * 100) / 100;
  };

  const loadProjects = async () => {
    console.log('loadProjects called, proposalsApiUrl:', proposalsApiUrl);
    if (!proposalsApiUrl) {
      console.warn('proposalsApiUrl is not set - proposals will not load');
      return [];
    }
    
    setLoadingProjects(true);
    try {
      // Try different API endpoints that might be used
      const endpoints = [
        `${proposalsApiUrl}?action=getProposals`,
        `${proposalsApiUrl}?action=getAllProposals`,
        `${proposalsApiUrl}`
      ];
      
      console.log('Trying endpoints:', endpoints);
      let data = null;
      for (const endpoint of endpoints) {
        try {
          console.log('Fetching from:', endpoint);
          const response = await fetch(endpoint);
          console.log('Response status:', response.status, response.statusText);
          const result = await response.json();
          console.log('Response data:', result);
          if (result.success && (result.proposals || result.data)) {
            console.log('Found proposals data:', result.proposals || result.data);
            data = result;
            break;
          } else {
            console.log('Response did not contain proposals:', result);
          }
        } catch (e) {
          console.error('Error fetching from endpoint:', endpoint, e);
          continue;
        }
      }
      
      if (data && (data.proposals || data.data)) {
        const proposals = data.proposals || data.data || [];
        console.log('Processing proposals:', proposals.length, 'proposals found');
        // Extract unique project numbers and client names
        const projects = proposals
          .filter(p => p.projectNumber && p.clientName)
          .map(p => {
            // Handle dates - they might come as Date objects, ISO strings, or YYYY-MM-DD strings
            // CRITICAL: Dates from Google Sheets API are often UTC Date objects or ISO strings
            // We need to extract the date part WITHOUT timezone conversion
            let startDate = p.startDate || '';
            let endDate = p.endDate || '';
            
            // If dates are Date objects, extract date components BEFORE any timezone conversion
            if (startDate instanceof Date) {
              // Use UTC components to get the actual date value, not the local timezone representation
              const year = startDate.getUTCFullYear();
              const month = String(startDate.getUTCMonth() + 1).padStart(2, '0');
              const day = String(startDate.getUTCDate()).padStart(2, '0');
              startDate = `${year}-${month}-${day}`;
              console.log('Converted Date object to string:', { original: startDate.toISOString(), result: startDate });
            } else if (typeof startDate === 'string') {
              if (startDate.includes('T')) {
                // If it's an ISO string (e.g., "2026-02-15T00:00:00.000Z"), extract just the date part
                // This represents the date in UTC, so we use the date part directly
                startDate = startDate.split('T')[0];
                console.log('Extracted date from ISO string:', startDate);
              } else if (!startDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                // If it's some other format, try to parse it
                const tempDate = new Date(startDate);
                if (!isNaN(tempDate.getTime())) {
                  const year = tempDate.getUTCFullYear();
                  const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
                  const day = String(tempDate.getUTCDate()).padStart(2, '0');
                  startDate = `${year}-${month}-${day}`;
                  console.log('Parsed date string:', { original: startDate, result: startDate });
                }
              }
            }
            
            if (endDate instanceof Date) {
              const year = endDate.getUTCFullYear();
              const month = String(endDate.getUTCMonth() + 1).padStart(2, '0');
              const day = String(endDate.getUTCDate()).padStart(2, '0');
              endDate = `${year}-${month}-${day}`;
              console.log('Converted Date object to string:', { original: endDate.toISOString(), result: endDate });
            } else if (typeof endDate === 'string') {
              if (endDate.includes('T')) {
                endDate = endDate.split('T')[0];
                console.log('Extracted date from ISO string:', endDate);
              } else if (!endDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const tempDate = new Date(endDate);
                if (!isNaN(tempDate.getTime())) {
                  const year = tempDate.getUTCFullYear();
                  const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
                  const day = String(tempDate.getUTCDate()).padStart(2, '0');
                  endDate = `${year}-${month}-${day}`;
                  console.log('Parsed date string:', { original: endDate, result: endDate });
                }
              }
            }
            
            return {
              projectNumber: String(p.projectNumber).trim(),
              clientName: p.clientName || '',
              venueName: p.venueName || '',
              startDate: startDate,
              endDate: endDate,
              city: p.city || '',
              state: p.state || '',
              version: p.version || 1,
              programId: p.programId ? String(p.programId).trim() : '',
              programName: p.programName ? String(p.programName).trim() : '',
              status: p.status || '',
              // Store full proposal data for invoice total calculation
              fullProposal: p
            };
          })
          .reduce((acc, p) => {
            // Keep only the highest version of each project (don't rely on API row order)
            const entry = {
              projectNumber: p.projectNumber,
              clientName: p.clientName,
              venueName: p.venueName,
              startDate: p.startDate,
              endDate: p.endDate,
              city: p.city,
              state: p.state,
              version: p.version || 1,
              programId: p.programId || '',
              programName: p.programName || '',
              status: p.status || '',
              fullProposal: p.fullProposal
            };
            const existingIndex = acc.findIndex(proj => proj.projectNumber === p.projectNumber);
            if (existingIndex === -1) {
              acc.push(entry);
            } else if ((entry.version || 1) > (acc[existingIndex].version || 1)) {
              acc[existingIndex] = entry;
            }
            return acc;
          }, [])
          .sort((a, b) => {
            // Sort by start date (most recent first), then by project number
            const aDate = a.startDate ? new Date(a.startDate).getTime() : 0;
            const bDate = b.startDate ? new Date(b.startDate).getTime() : 0;
            if (aDate !== bDate) {
              return bDate - aDate;
            }
            const aNum = parseInt(a.projectNumber);
            const bNum = parseInt(b.projectNumber);
            if (!isNaN(aNum) && !isNaN(bNum)) {
              return bNum - aNum; // Descending
            }
            return b.projectNumber.localeCompare(a.projectNumber);
          });
        
        console.log('Setting available projects:', projects.length, 'projects');
        setAvailableProjects(projects);
        return projects;
      } else {
        console.warn('No proposals data found in response');
        return [];
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      // Silently fail - this is optional functionality
      return [];
    } finally {
      setLoadingProjects(false);
    }
  };

  const applyProposalDataToWorkingQuote = (project, { preserveCalculatorInputs = true } = {}) => {
    if (!project) return false;
    const inv = calculateInvoiceTotalFromProposal(project);
    const prod = calculateProductTotalFromProposal(project);
    if (inv == null && prod == null) return false;

    // Reference prior saved quote totals for subtle "Previously $X" hints.
    const latestSaved = projectVersions[0] || null;
    if (latestSaved) {
      setPriorInvoiceTotal(roundMoney(latestSaved.invoiceTotal));
      setPriorProductTotal(roundMoney(latestSaved.productTotal));
    } else {
      setPriorInvoiceTotal(null);
      setPriorProductTotal(null);
    }

    setSelectedProjectData(project);
    setProjectVersion(getMaxSavedVersion() + 1);
    setResults(null);
    setError('');
    setSaveSuccess(false);

    if (!preserveCalculatorInputs) {
      setIsOvernightRequired('');
      setNumberOfNights('');
      setNumberOfTeamMembers('');
      setNightsManuallySet(false);
      setLodgingRooms('');
      setLodgingNights('');
      setLodgingTotal('');
      setLodgingRoomsManuallySet(false);
      setLodgingNightsManuallySet(false);
      setLodgingTotalManuallySet(false);
      setGasTotal('');
      setFlightsCount('');
      setFlightsTotal('');
      setPerDiemSupervisors('');
      setPerDiemTeamMembers('');
      setPerDiemTotal('');
      setPerDiemTotalManuallySet(false);
      setTruckRentalRequired('');
      setTruckRentalCount('');
      setTruckRentalTotal('');
      setOtherExpenses([{ name: '', total: '' }]);
      setDistance(null);
      setMarket(null);
    }

    setInvoiceTotal(inv != null ? toMoneyInputString(inv) : '');
    setProductTotal(prod != null ? toMoneyInputString(prod) : '');
    setInvoiceTotalManuallySet(false);
    setProductTotalManuallySet(false);
    if (project.startDate) setDepartureDate(String(project.startDate).split('T')[0]);
    if (project.endDate) setReturnDate(String(project.endDate).split('T')[0]);
    if (project.startDate && project.endDate) {
      const eventNights = calculateNightsFromDates(project.startDate, project.endDate);
      setEventDaysInput(
        eventNights !== null ? String(Math.max(1, eventNights + 1)) : '1'
      );
    } else {
      setEventDaysInput('1');
    }
    setDepartureDateManuallySet(false);
    setReturnDateManuallySet(false);
    if (project.city && project.state) {
      setDeliveryAddress(`${project.city}, ${project.state}`);
    }
    if (project.clientName) setClientName(project.clientName);
    // Keep event type if user already chose Non-Standard; otherwise Standard.
    if (!preserveCalculatorInputs || !eventType) {
      setEventType('Standard');
    }
    setLoadedProposalFingerprint(buildProposalFingerprint(project));
    setProposalDriftWhileDrafting(false);
    setWorkingQuoteReady(true);
    setProposalRefreshedAt(new Date());
    return true;
  };

  // Load Google Maps API dynamically
  useEffect(() => {
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    
    if (!GOOGLE_MAPS_API_KEY) {
      console.warn('Google Maps API key not configured. Address autocomplete will be disabled.');
      return;
    }

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', initializeAutocomplete);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initializeAutocomplete;
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
        setError('Failed to load Google Maps. Please check your API key configuration.');
      };
      document.head.appendChild(script);
    } else {
      initializeAutocomplete();
    }

    function initializeAutocomplete() {
      const checkInput = () => {
        if (inputRef.current && window.google && window.google.maps && window.google.maps.places) {
          try {
            if (inputRef.current.dataset.autocompleteInitialized === 'true') {
              return;
            }
            
            const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
              types: ['address'],
              componentRestrictions: { country: 'us' }
            });

            inputRef.current.dataset.autocompleteInitialized = 'true';

            autocomplete.addListener('place_changed', () => {
              const place = autocomplete.getPlace();
              if (place.formatted_address) {
                setDeliveryAddress(place.formatted_address);
                // Extract timezone from address
                const timezone = getTimezoneFromAddress(place.formatted_address);
                setDeliveryTimezone(timezone);
                setError('');
                // Trigger date recalculation if project is selected
                // This will happen via the useEffect hook below
              }
            });

            autocompleteRef.current = autocomplete;
          } catch (error) {
            console.warn('Failed to initialize Google Places Autocomplete:', error);
          }
        } else if (inputRef.current && !window.google) {
          setTimeout(checkInput, 100);
        }
      };
      
      checkInput();
      setTimeout(checkInput, 100);
    }
  }, []);

  const calculateNightsFromDates = (departure, returnDate) => {
    if (!departure || !returnDate) return null;
    
    const departureTime = new Date(departure).getTime();
    const returnTime = new Date(returnDate).getTime();
    
    if (returnTime <= departureTime) return 0;
    
    const diffTime = returnTime - departureTime;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const formatDateForInput = (dateString, timezone = null) => {
    if (!dateString) return '';
    try {
      console.log('formatDateForInput called with:', { dateString, timezone, type: typeof dateString });
      
      // Handle different date formats
      let date;
      if (typeof dateString === 'string') {
        // Try parsing as-is first (might already be YYYY-MM-DD)
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Parse YYYY-MM-DD as a date-only value
          // CRITICAL: Don't create a Date object - just use the string directly
          // Date objects cause timezone conversion issues
          // For date-only values, we should return them as-is
          console.log(`formatDateForInput: YYYY-MM-DD date ${dateString} - returning as-is`);
          return dateString; // Return immediately without creating Date object
        } else if (dateString.match(/^\d{4}-\d{2}-\d{2}T/)) {
          // Handle ISO date strings (e.g., "2026-02-07T00:00:00.000Z")
          // Extract just the date part and parse as local date
          const datePart = dateString.split('T')[0];
          const [year, month, day] = datePart.split('-').map(Number);
          date = new Date(year, month - 1, day);
          console.log(`Parsed ISO date ${dateString} as local date:`, date.toISOString());
        } else {
          // For other formats, try to parse and extract date components
          const tempDate = new Date(dateString);
          if (!isNaN(tempDate.getTime())) {
            // CRITICAL: When parsing a date string that might be in UTC,
            // we need to extract the date components using the event location's timezone
            // or use UTC components if it's a UTC date string
            let year, month, day;
            
            if (dateString.includes('Z') || dateString.includes('UTC')) {
              // It's a UTC date - use UTC components
              year = tempDate.getUTCFullYear();
              month = tempDate.getUTCMonth();
              day = tempDate.getUTCDate();
              console.log(`Parsed UTC date ${dateString}, using UTC components:`, { year, month: month + 1, day });
            } else if (timezone && typeof Intl !== 'undefined') {
              // Use the event location's timezone to extract date components
              const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              });
              const parts = formatter.formatToParts(tempDate);
              year = parseInt(parts.find(p => p.type === 'year').value);
              month = parseInt(parts.find(p => p.type === 'month').value) - 1;
              day = parseInt(parts.find(p => p.type === 'day').value);
              console.log(`Parsed date ${dateString} in timezone ${timezone}:`, { year, month: month + 1, day });
            } else {
              // Fallback: use local date components
              year = tempDate.getFullYear();
              month = tempDate.getMonth();
              day = tempDate.getDate();
              console.log(`Parsed date ${dateString} using local components:`, { year, month: month + 1, day });
            }
            date = new Date(year, month, day);
          } else {
            console.warn('Could not parse date string, trying fallback:', dateString);
            // Try one more time with a different approach - maybe it's a date string with slashes
            if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
              const [month, day, year] = dateString.split('/').map(Number);
              date = new Date(year, month - 1, day);
            } else {
              console.error('Failed to parse date:', dateString);
              return '';
            }
          }
        }
      } else if (dateString instanceof Date) {
        // If it's already a Date object, extract components to avoid timezone issues
        // CRITICAL: If the Date was created from a UTC string, we need to use UTC components
        // Otherwise, if it's a local date, use local components
        // Check if this looks like a UTC date by checking if it's close to midnight UTC
        const utcHours = dateString.getUTCHours();
        const isLikelyUTC = utcHours === 0 || utcHours === 23 || utcHours === 1;
        
        let year, month, day;
        if (isLikelyUTC && timezone && typeof Intl !== 'undefined') {
          // Use the event location's timezone to extract date components
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
          const parts = formatter.formatToParts(dateString);
          year = parseInt(parts.find(p => p.type === 'year').value);
          month = parseInt(parts.find(p => p.type === 'month').value) - 1;
          day = parseInt(parts.find(p => p.type === 'day').value);
          console.log(`Extracted from Date object (UTC-like) using timezone ${timezone}:`, { year, month: month + 1, day });
        } else {
          // Use local date components
          year = dateString.getFullYear();
          month = dateString.getMonth();
          day = dateString.getDate();
          console.log(`Extracted from Date object (local):`, { year, month: month + 1, day });
        }
        date = new Date(year, month, day);
      } else {
        console.warn('Unexpected date format:', typeof dateString, dateString);
        return '';
      }
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date after parsing:', dateString);
        return '';
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const result = `${year}-${month}-${day}`;
      console.log(`formatDateForInput result: ${result}`);
      return result;
    } catch (e) {
      console.error('Error formatting date:', e, dateString);
      return '';
    }
  };

  const addDays = (dateString, days) => {
    if (!dateString) return '';
    try {
      // CRITICAL: For YYYY-MM-DD strings, do date arithmetic directly without Date objects
      // This avoids timezone conversion issues
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-').map(Number);
        // Create a date object for calculation, but use it only for arithmetic
        // Then extract components directly without timezone conversion
        const date = new Date(year, month - 1, day);
        date.setDate(date.getDate() + days);
        
        // Extract components directly - these are already in the correct date
        const resultYear = date.getFullYear();
        const resultMonth = String(date.getMonth() + 1).padStart(2, '0');
        const resultDay = String(date.getDate()).padStart(2, '0');
        const result = `${resultYear}-${resultMonth}-${resultDay}`;
        console.log(`addDays: ${dateString} + ${days} days = ${result}`);
        return result;
      }
      
      // For other formats, try to parse
      let date;
      if (typeof dateString === 'string') {
        date = new Date(dateString);
      } else {
        date = dateString;
      }
      
      if (isNaN(date.getTime())) {
        console.error('addDays: Invalid date:', dateString);
        return dateString;
      }
      
      // Extract components before adding days to avoid timezone issues
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      
      // Create new date and add days
      const newDate = new Date(year, month, day);
      newDate.setDate(newDate.getDate() + days);
      
      // Format directly from the date object
      const resultYear = newDate.getFullYear();
      const resultMonth = String(newDate.getMonth() + 1).padStart(2, '0');
      const resultDay = String(newDate.getDate()).padStart(2, '0');
      const result = `${resultYear}-${resultMonth}-${resultDay}`;
      console.log(`addDays: ${dateString} + ${days} days = ${result}`);
      return result;
    } catch (e) {
      console.error('addDays error:', e, dateString);
      return dateString;
    }
  };

  const calculateDatesFromMarket = (startDate, endDate, marketName, timezone = null) => {
    if (!startDate || !endDate) return { departure: '', return: '' };
    
    console.log('calculateDatesFromMarket called with:', { startDate, endDate, marketName, timezone });
    
    // Normalize dates to YYYY-MM-DD format first
    // Dates are interpreted in the event location's timezone context
    const normalizedStart = formatDateForInput(startDate, timezone);
    const normalizedEnd = formatDateForInput(endDate, timezone);
    
    console.log('Normalized dates:', { normalizedStart, normalizedEnd });
    
    if (!normalizedStart || !normalizedEnd) {
      console.error('Failed to normalize dates:', { startDate, endDate, normalizedStart, normalizedEnd });
      return { departure: '', return: '' };
    }
    
    let departure = normalizedStart;
    let returnDate = normalizedEnd;
    
    if (marketName === 'local') {
      // Local: departure = start date, return = end date
      departure = normalizedStart;
      returnDate = normalizedEnd;
      console.log('Local market - no date adjustments');
    } else if (marketName === 'regional') {
      // Regional: departure = start date, return = end date + 1 day
      departure = normalizedStart;
      returnDate = addDays(normalizedEnd, 1);
      console.log('Regional market - return date +1 day');
    } else if (marketName === 'extended') {
      // Extended: departure = start date - 1 day, return = end date + 1 day
      departure = addDays(normalizedStart, -1);
      returnDate = addDays(normalizedEnd, 1);
      console.log('Extended market - departure -1 day, return +1 day');
    } else {
      // Default to Extended logic for stretch or unknown markets
      departure = addDays(normalizedStart, -1);
      returnDate = addDays(normalizedEnd, 1);
      console.log('Unknown market - defaulting to Extended logic');
    }
    
    console.log('Final calculated dates:', { departure, return: returnDate, marketName });
    
    return { departure, return: returnDate };
  };

  useEffect(() => {
    if (isOvernightRequired === 'yes' && departureDate && returnDate && !nightsManuallySet) {
      const calculatedNights = calculateNightsFromDates(departureDate, returnDate);
      if (calculatedNights !== null && calculatedNights >= 0) {
        setNumberOfNights(calculatedNights.toString());
      }
    }
  }, [departureDate, returnDate, isOvernightRequired, nightsManuallySet]);

  // Auto-calculate Per Diem total when supervisor / team member counts change
  useEffect(() => {
    if (!perDiemTotalManuallySet && eventType === 'Non-Standard Delivery') {
      const nights = parseInt(numberOfNights) || 0;
      const days = nights > 0 ? nights + 1 : 1; // nights + 1; default 1 day
      const calculatedTotal = calculatePerDiemTotal(
        perDiemSupervisors,
        perDiemTeamMembers,
        days
      );
      if (calculatedTotal > 0) {
        setPerDiemTotal(calculatedTotal.toFixed(2));
      } else if (!perDiemSupervisors && !perDiemTeamMembers) {
        setPerDiemTotal('');
      }
    }
  }, [perDiemSupervisors, perDiemTeamMembers, perDiemTotalManuallySet, eventType, numberOfNights]);

  // Auto-populate lodging nights from main numberOfNights field
  useEffect(() => {
    if (eventType === 'Non-Standard Delivery' && !lodgingNightsManuallySet) {
      if (numberOfNights) {
        setLodgingNights(numberOfNights);
      } else {
        // Clear if numberOfNights is cleared
        setLodgingNights('');
      }
    } else if (eventType !== 'Non-Standard Delivery') {
      // Clear when switching away from Non-Standard
      setLodgingNights('');
      setLodgingNightsManuallySet(false);
    }
  }, [numberOfNights, eventType, lodgingNightsManuallySet]);

  // Auto-calculate lodging rooms from team members (divided by 2, rounded up)
  useEffect(() => {
    if (eventType === 'Non-Standard Delivery' && !lodgingRoomsManuallySet) {
      const teamCount = parseInt(numberOfTeamMembers) || 0;
      if (teamCount > 0) {
        const calculatedRooms = Math.ceil(teamCount / 2);
        setLodgingRooms(calculatedRooms.toString());
      } else {
        // Clear if numberOfTeamMembers is cleared
        setLodgingRooms('');
      }
    } else if (eventType !== 'Non-Standard Delivery') {
      // Clear when switching away from Non-Standard
      setLodgingRooms('');
      setLodgingRoomsManuallySet(false);
    }
  }, [numberOfTeamMembers, eventType, lodgingRoomsManuallySet]);

  // Auto-calculate lodging total: $250 per room per night
  useEffect(() => {
    if (!lodgingTotalManuallySet && eventType === 'Non-Standard Delivery') {
      const rooms = parseInt(lodgingRooms) || 0;
      const nights = parseInt(lodgingNights) || 0;
      if (rooms > 0 && nights > 0) {
        const calculatedTotal = rooms * nights * 250;
        setLodgingTotal(calculatedTotal.toFixed(2));
      } else if (!lodgingRooms && !lodgingNights) {
        setLodgingTotal('');
      }
    }
  }, [lodgingRooms, lodgingNights, eventType, lodgingTotalManuallySet]);

  // Auto-update dates when delivery address is entered and project is selected
  useEffect(() => {
    // Only recalculate if:
    // 1. Project is selected with dates
    // 2. Delivery address is entered
    // 3. Dates haven't been manually set
    // 4. We have Google Maps API available
    if (
      selectedProjectData && 
      selectedProjectData.startDate && 
      selectedProjectData.endDate &&
      deliveryAddress &&
      deliveryAddress.trim().length > 0 &&
      (!departureDateManuallySet || !returnDateManuallySet) &&
      window.google && 
      window.google.maps
    ) {
      // Debounce the distance calculation to avoid too many API calls
      const timeoutId = setTimeout(async () => {
        try {
          const calculatedDistance = await calculateDistance(deliveryAddress);
          const determinedMarket = determineMarket(calculatedDistance);
          
          // Only update if we got a valid market
          if (determinedMarket && determinedMarket.name) {
            // Update market and distance state
            setMarket(determinedMarket);
            setDistance(calculatedDistance);
            
            // Get timezone from project data (city/state) or delivery address
            const projectTimezone = selectedProjectData.city && selectedProjectData.state
              ? getTimezoneFromAddress(`${selectedProjectData.city}, ${selectedProjectData.state}`)
              : deliveryTimezone;
            
            const calculatedDates = calculateDatesFromMarket(
              selectedProjectData.startDate,
              selectedProjectData.endDate,
              determinedMarket.name.toLowerCase(),
              projectTimezone
            );
            
            // Update dates only if they haven't been manually set
            if (!departureDateManuallySet && calculatedDates.departure) {
              setDepartureDate(calculatedDates.departure);
            }
            if (!returnDateManuallySet && calculatedDates.return) {
              setReturnDate(calculatedDates.return);
            }
          }
        } catch (error) {
          // Silently fail - user can still click Calculate manually
          console.log('Auto-calculation failed, will calculate on button click:', error.message);
        }
      }, 1000); // 1 second debounce

      return () => clearTimeout(timeoutId);
    }
  }, [deliveryAddress, selectedProjectData, departureDateManuallySet, returnDateManuallySet]);

  const calculateDistance = (destinationAddress) => calculateDrivingDistance(destinationAddress);

  const determineMarket = (distance) => determineMarketByDistance(distance);

  const calculateFees = (marketToUse, calculatedDistance) => {
    const invoice = parseFloat(invoiceTotal);
    const product = parseFloat(productTotal) || invoice; // Use productTotal if provided, otherwise fallback to invoiceTotal
    const nights = parseInt(numberOfNights) || 0;
    const teamMembers = parseInt(numberOfTeamMembers) || 0;
    const overnightRequired = isOvernightRequired === 'yes';

    if (!marketToUse || calculatedDistance === null || calculatedDistance === undefined) {
      return null;
    }

    if (invoice < marketToUse.rentalMinimum) {
      return {
        error: `Invoice total must be at least $${marketToUse.rentalMinimum.toLocaleString()} for ${marketToUse.name} market`,
        standardFee: 0,
        extendedFee: 0,
        totalFee: 0
      };
    }

    // Team travel days (warehouse departure → return) — overnight / cost context only.
    const nightsFromDates = calculateNightsFromDates(departureDate, returnDate);
    const teamGoneDays = nightsFromDates !== null
      ? Math.max(1, nightsFromDates + 1)
      : (overnightRequired ? Math.max(1, nights + 1) : 1);

    // Event days (load-in → strike) — complimentary eligibility only.
    // Never fall back to team-gone days; that conflates the two clocks.
    const eventNights = (selectedProjectData && selectedProjectData.startDate && selectedProjectData.endDate)
      ? calculateNightsFromDates(selectedProjectData.startDate, selectedProjectData.endDate)
      : null;
    const eventDaysFromProject =
      eventNights !== null ? Math.max(1, eventNights + 1) : null;
    const eventDays =
      eventDaysFromProject != null
        ? eventDaysFromProject
        : Math.max(1, parseInt(eventDaysInput, 10) || 1);

    const feeEval = evaluateStandardDeliveryFee({
      invoice,
      product,
      market: marketToUse,
      eventDays,
    });
    const {
      standardFee,
      amountToThreshold,
      freeThresholdApplied,
      complimentaryBlockedByMultiDay,
    } = feeEval;

    const overnightEval = evaluateExtendedOvernightFee({
      market: marketToUse,
      nights,
      teamMembers,
      overnightRequired,
    });
    const extendedFee = overnightEval.extendedFee;
    const extendedFeeExplanation = overnightEval.explanation
      ? `${overnightEval.explanation} = $${extendedFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : null;

    const totalFee = standardFee === 'waived' || standardFee === null 
      ? extendedFee 
      : (typeof standardFee === 'number' ? standardFee : 0) + extendedFee;

    return {
      standardFee,
      extendedFee,
      totalFee,
      days: teamGoneDays,
      eventDays,
      eventDaysSource: eventDaysFromProject != null ? 'project' : 'manual',
      nights: overnightRequired ? nights : 0,
      teamMembers,
      distance: calculatedDistance.toFixed(1),
      market: marketToUse.name,
      amountToThreshold,
      thresholdDays: eventDays,
      freeThresholdApplied,
      complimentaryBlockedByMultiDay,
      freeThresholdPerDay: marketToUse.freeThreshold != null ? marketToUse.freeThreshold : null,
      extendedFeeExplanation
    };
  };

  const handleCalculate = async () => {
    setError('');
    setResults(null);
    setLoading(true);

    if (!invoiceTotal || parseFloat(invoiceTotal) <= 0) {
      setError('Please enter a valid invoice total');
      setLoading(false);
      return;
    }

    if (eventType === 'Non-Standard Delivery') {
      // For Non-Standard, validate that at least one cost field is filled
      const hasAnyCost = 
        (lodgingTotal && parseFloat(lodgingTotal) > 0) ||
        (gasTotal && parseFloat(gasTotal) > 0) ||
        (flightsTotal && parseFloat(flightsTotal) > 0) ||
        (perDiemTotal && parseFloat(perDiemTotal) > 0) ||
        (truckRentalTotal && parseFloat(truckRentalTotal) > 0) ||
        otherExpenses.some(exp => exp.total && parseFloat(exp.total) > 0);
      
      if (!hasAnyCost) {
        setError('Please enter at least one delivery cost item');
        setLoading(false);
        return;
      }

      // Calculate distance and market if delivery address is provided
      let calculatedDistance = null;
      let determinedMarket = null;
      if (deliveryAddress) {
        try {
          calculatedDistance = await calculateDistance(deliveryAddress);
          if (calculatedDistance !== null && calculatedDistance !== undefined) {
            determinedMarket = determineMarket(calculatedDistance);
          }
        } catch (err) {
          console.error('Error calculating distance for non-standard:', err);
          // Continue without distance/market if calculation fails
        }
      }

      // Calculate manual delivery cost for Non-Standard
      const calculateManualTotal = () => {
        const lodging = parseFloat(lodgingTotal) || 0;
        const gas = parseFloat(gasTotal) || 0;
        const flights = parseFloat(flightsTotal) || 0;
        const perDiem = parseFloat(perDiemTotal) || 0;
        const truckRental = parseFloat(truckRentalTotal) || 0;
        const other = otherExpenses.reduce((sum, exp) => sum + (parseFloat(exp.total) || 0), 0);
        return lodging + gas + flights + perDiem + truckRental + other;
      };
      
      const manualTotal = calculateManualTotal();
      
      // Calculate days from dates if available
      let calculatedDays = null;
      if (departureDate && returnDate) {
        const daysCalc = calculateNightsFromDates(departureDate, returnDate);
        calculatedDays = daysCalc !== null ? daysCalc + 1 : null; // days = nights + 1
      }
      
      // Complimentary Delivery Threshold — event days only (never team-gone / calculatedDays).
      let amountToThreshold = null;
      let nonStandardEventDays = Math.max(1, parseInt(eventDaysInput, 10) || 1);
      let complimentaryBlockedByMultiDay = false;
      let freeThresholdApplied = null;
      if (determinedMarket && (productTotal || invoiceTotal)) {
        const product = parseFloat(productTotal) || parseFloat(invoiceTotal);
        const eventNights = (selectedProjectData && selectedProjectData.startDate && selectedProjectData.endDate)
          ? calculateNightsFromDates(selectedProjectData.startDate, selectedProjectData.endDate)
          : null;
        nonStandardEventDays =
          eventNights !== null
            ? Math.max(1, eventNights + 1)
            : Math.max(1, parseInt(eventDaysInput, 10) || 1);
        const feeEval = evaluateStandardDeliveryFee({
          invoice: parseFloat(invoiceTotal) || product,
          product,
          market: determinedMarket,
          eventDays: nonStandardEventDays,
        });
        amountToThreshold = feeEval.amountToThreshold;
        complimentaryBlockedByMultiDay = feeEval.complimentaryBlockedByMultiDay;
        freeThresholdApplied = feeEval.freeThresholdApplied;
      }
      
      // Calculate Per Diem/Day Rate explanation
      const nights = parseInt(numberOfNights) || 0;
      const days = calculatedDays || (nights > 0 ? nights + 1 : null);
      const perDiemExplanation =
        days != null
          ? formatPerDiemExplanation(perDiemSupervisors, perDiemTeamMembers, days)
          : null;
      
      const feeResults = {
        standardFee: manualTotal > 0 ? manualTotal : 0,
        extendedFee: 0,
        totalFee: manualTotal,
        days: calculatedDays,
        eventDays: nonStandardEventDays,
        eventDaysSource:
          selectedProjectData && selectedProjectData.startDate && selectedProjectData.endDate
            ? 'project'
            : 'manual',
        thresholdDays: nonStandardEventDays,
        nights: parseInt(numberOfNights) || 0,
        teamMembers: parseInt(numberOfTeamMembers) || 0,
        market: determinedMarket ? determinedMarket.name : null,
        distance: calculatedDistance !== null && calculatedDistance !== undefined ? calculatedDistance.toFixed(1) : null,
        amountToThreshold,
        freeThresholdApplied,
        complimentaryBlockedByMultiDay,
        extendedFeeExplanation: perDiemExplanation,
        isNonStandard: true,
        manualBreakdown: {
          lodging: parseFloat(lodgingTotal) || 0,
          gas: parseFloat(gasTotal) || 0,
          flights: parseFloat(flightsTotal) || 0,
          perDiem: parseFloat(perDiemTotal) || 0,
          truckRental: parseFloat(truckRentalTotal) || 0,
          other: otherExpenses.reduce((sum, exp) => sum + (parseFloat(exp.total) || 0), 0),
          otherExpenses: (() => {
            console.log('Processing otherExpenses for manualBreakdown:', otherExpenses);
            const filtered = otherExpenses.filter(exp => {
              const nameStr = exp.name ? String(exp.name).trim() : '';
              const hasName = nameStr.length > 0;
              // Parse total - it might be a formatted string like "$100.00" or a number string like "100.00"
              const totalStr = String(exp.total || '').replace(/[^\d.]/g, '');
              const totalValue = parseFloat(totalStr) || 0;
              const hasTotal = !isNaN(totalValue) && totalValue > 0;
              const shouldInclude = hasName || hasTotal;
              console.log('Filtering other expense:', { name: exp.name, total: exp.total, totalStr, nameStr, hasName, totalValue, hasTotal, shouldInclude });
              return shouldInclude;
            });
            console.log('Filtered otherExpenses result:', filtered);
            return filtered;
          })()
        }
      };

      console.log('Non-Standard fee results:', feeResults);
      setResults(feeResults);
      setLoading(false);
      // Scroll to results after a brief delay to ensure DOM update
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return;
    }

    // Standard delivery validation
    if (!deliveryAddress) {
      setError('Please enter a delivery address');
      setLoading(false);
      return;
    }

    if (isOvernightRequired === 'yes' && (!numberOfNights || parseInt(numberOfNights) < 1)) {
      setError('Please enter the number of nights');
      setLoading(false);
      return;
    }

    if (!numberOfTeamMembers || parseInt(numberOfTeamMembers) < 1) {
      setError('Please enter the number of team members');
      setLoading(false);
      return;
    }

    try {
      const calculatedDistance = await calculateDistance(deliveryAddress);
      
      if (calculatedDistance > 600) {
        setError('Delivery address is more than 600 miles from origin. This calculator is for destinations within 600 miles. Please reach out to JP for pricing guidance.');
        setLoading(false);
        return;
      }

      setDistance(calculatedDistance);
      const determinedMarket = determineMarket(calculatedDistance);
      setMarket(determinedMarket);
      
      // Auto-populate dates based on market if project data exists
      // Always calculate dates when Calculate is clicked (manual flags are reset on project change)
      if (selectedProjectData && selectedProjectData.startDate && selectedProjectData.endDate) {
        console.log('Selected project data:', selectedProjectData);
        console.log('Calculating dates from market:', {
          startDate: selectedProjectData.startDate,
          endDate: selectedProjectData.endDate,
          market: determinedMarket.name.toLowerCase(),
          startDateType: typeof selectedProjectData.startDate,
          endDateType: typeof selectedProjectData.endDate
        });
        // Get timezone from project data (city/state) or delivery address
        const projectTimezone = selectedProjectData.city && selectedProjectData.state
          ? getTimezoneFromAddress(`${selectedProjectData.city}, ${selectedProjectData.state}`)
          : deliveryTimezone;
        
        const calculatedDates = calculateDatesFromMarket(
          selectedProjectData.startDate,
          selectedProjectData.endDate,
          determinedMarket.name.toLowerCase(),
          projectTimezone
        );
        console.log('Calculated dates:', calculatedDates);
        // Only set dates if they haven't been manually edited since project selection
        if (!departureDateManuallySet && calculatedDates.departure) {
          console.log('Setting departure date:', calculatedDates.departure);
          setDepartureDate(calculatedDates.departure);
        }
        if (!returnDateManuallySet && calculatedDates.return) {
          console.log('Setting return date:', calculatedDates.return);
          setReturnDate(calculatedDates.return);
        }
      } else {
        console.log('Not calculating dates - missing data:', {
          hasProjectData: !!selectedProjectData,
          hasStartDate: !!(selectedProjectData && selectedProjectData.startDate),
          hasEndDate: !!(selectedProjectData && selectedProjectData.endDate)
        });
      }
      
      const feeResults = calculateFees(determinedMarket, calculatedDistance);
      
      if (!feeResults) {
        setError('Unable to calculate fees. Please try again.');
        setLoading(false);
        return;
      }
      
      if (feeResults.error) {
        setError(feeResults.error);
        setLoading(false);
        return;
      }

      console.log('Fee results:', feeResults);
      setResults(feeResults);
      setLoading(false);
      // Scroll to results after a brief delay to ensure DOM update
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } catch (err) {
      console.error('Calculation error:', err);
      setError(err.message || 'Error calculating distance. Please check the address and try again.');
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === 'waived') return amount;
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format a decimal rate (e.g. 0.125) as a percent without misleading rounding (→ "12.5%").
  const formatPercent = (rate) => {
    if (rate === null || rate === undefined) return '';
    const pct = rate * 100;
    return `${pct.toFixed(2).replace(/\.?0+$/, '')}%`;
  };

  const handleSave = async () => {
    if (!results || results.isHistorical) return;
    if (!workingQuoteReady) {
      setError('Refresh proposal data before saving a new delivery quote.');
      return;
    }
    
    setSaving(true);
    try {
      // Build calculation data differently for Standard vs Non-Standard
      let calculationData;
      
        if (eventType === 'Non-Standard Delivery') {
        calculationData = {
          timestamp: new Date().toISOString(),
          projectNumber: projectNumber || '',
          version: projectVersion,
          clientName: clientName || 'N/A',
          eventType: 'Non-Standard Delivery',
          invoiceTotal: parseFloat(invoiceTotal) || 0,
          productTotal: parseFloat(productTotal) || parseFloat(invoiceTotal) || 0,
          deliveryAddress: deliveryAddress || '',
          departureDate: departureDate || '',
          returnDate: returnDate || '',
          isOvernightRequired: isOvernightRequired || '',
          numberOfNights: parseInt(numberOfNights) || 0,
          numberOfTeamMembers: parseInt(numberOfTeamMembers) || 0,
          distance: results.distance || null,
          market: results.market || null,
          standardFee: null,
          extendedFee: null,
          totalFee: results.totalFee || 0,
          days: results.days || null,
          nights: results.nights || 0,
          teamMembers: results.teamMembers || parseInt(numberOfTeamMembers) || 0,
          amountToThreshold: results.amountToThreshold !== null && results.amountToThreshold !== undefined ? results.amountToThreshold : null,
          eventDays: results.eventDays != null ? results.eventDays : null,
          thresholdDays: results.thresholdDays != null ? results.thresholdDays : results.eventDays ?? null,
          freeThresholdApplied: results.freeThresholdApplied != null ? results.freeThresholdApplied : null,
          complimentaryBlockedByMultiDay: !!results.complimentaryBlockedByMultiDay,
          extendedFeeExplanation: results.extendedFeeExplanation || '',
          ...(workingQuoteReady ? { created_from: 'proposal_sync' } : {}),
          manualCosts: {
            lodging: {
              rooms: parseInt(lodgingRooms) || 0,
              nights: parseInt(lodgingNights) || 0,
              total: parseFloat(lodgingTotal) || 0
            },
            gas: {
              total: parseFloat(gasTotal) || 0
            },
            flights: {
              count: parseInt(flightsCount) || 0,
              total: parseFloat(flightsTotal) || 0
            },
            perDiem: {
              supervisors: parseInt(perDiemSupervisors) || 0,
              teamMembers: parseInt(perDiemTeamMembers) || 0,
              total: parseFloat(perDiemTotal) || 0
            },
            truckRental: {
              required: truckRentalRequired === 'yes',
              count: parseInt(truckRentalCount) || 0,
              total: parseFloat(truckRentalTotal) || 0
            },
            otherExpenses: otherExpenses.filter(exp => exp.name || exp.total)
          }
        };
      } else {
        // For Standard Delivery: use standard fee structure, no manual costs
        calculationData = {
          timestamp: new Date().toISOString(),
          projectNumber: projectNumber || '',
          version: projectVersion,
          clientName: clientName || 'N/A',
          eventType: 'Standard',
          invoiceTotal: parseFloat(invoiceTotal) || 0,
          productTotal: parseFloat(productTotal) || parseFloat(invoiceTotal) || 0,
          deliveryAddress: deliveryAddress || '',
          departureDate: departureDate || '',
          returnDate: returnDate || '',
          isOvernightRequired: isOvernightRequired || '',
          numberOfNights: parseInt(numberOfNights) || 0,
          numberOfTeamMembers: parseInt(numberOfTeamMembers) || 0,
          distance: results.distance || null,
          market: results.market || null,
          standardFee: results.standardFee || 0,
          extendedFee: results.extendedFee || 0,
          totalFee: results.totalFee || 0,
          days: results.days || null,
          nights: results.nights || 0,
          teamMembers: results.teamMembers || parseInt(numberOfTeamMembers) || 0,
          amountToThreshold: results.amountToThreshold !== null && results.amountToThreshold !== undefined ? results.amountToThreshold : null,
          eventDays: results.eventDays != null ? results.eventDays : null,
          thresholdDays: results.thresholdDays != null ? results.thresholdDays : results.eventDays ?? null,
          freeThresholdApplied: results.freeThresholdApplied != null ? results.freeThresholdApplied : null,
          complimentaryBlockedByMultiDay: !!results.complimentaryBlockedByMultiDay,
          extendedFeeExplanation: results.extendedFeeExplanation || '',
          ...(workingQuoteReady ? { created_from: 'proposal_sync' } : {})
        };
      }
      
      console.log('Saving calculation data:', calculationData);
      console.log('Event Type:', calculationData.eventType);
      
      if (onSaveCalculation) {
        const savedVersionNumber = projectVersion;
        await onSaveCalculation(calculationData);
        setSaving(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 4000);
        // Return to saved-quote state; new quote is now immutable history.
        setSelectedVersion(savedVersionNumber);
        setHistoryDetailsOpen(false);
        setResults(null);
        setWorkingQuoteReady(false);
        setLoadedProposalFingerprint('');
        setProposalDriftWhileDrafting(false);
        setProposalRefreshedAt(null);
        setPriorInvoiceTotal(null);
        setPriorProductTotal(null);
        setError('');
        autoRefreshedProjectRef.current = '';
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save calculation. Please try again.');
      setSaving(false);
    }
  };

  const selectedSnapshot =
    selectedVersion != null && projectVersions.length > 0
      ? projectVersions.find((v) => (parseInt(v.version, 10) || 1) === selectedVersion) || null
      : null;

  const handleRefreshProposalData = async ({ preserveCalculatorInputs = true, scrollToForm = true } = {}) => {
    const projectKey = String(projectNumber || projectNumberNorm || '').trim();
    if (!projectKey) {
      setError('Select a project first.');
      return false;
    }
    setProposalRefreshedAt(null);
    const projects = await loadProjects();
    const fresh = (projects || []).find(
      (p) => String(p.projectNumber).trim() === projectKey
    );
    const project = fresh || selectedProjectData;
    if (!project) {
      setProposalRefreshedAt(null);
      setError('Select a project first.');
      return false;
    }
    const applied = applyProposalDataToWorkingQuote(project, { preserveCalculatorInputs });
    if (!applied) {
      setProposalRefreshedAt(null);
      setError('Current proposal data is unavailable for this project.');
      return false;
    }
    if (scrollToForm) {
      requestAnimationFrame(() => {
        if (newQuoteFormRef.current) {
          newQuoteFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
    return true;
  };

  const handleSelectHistoryVersion = (versionValue) => {
    const version = versionValue ? parseInt(versionValue, 10) : null;
    if (version == null || Number.isNaN(version)) return;
    setSelectedVersion(version);
    setHistoryDetailsOpen(false);
  };

  const formatProposalRefreshLabel = (at) => {
    if (!at) return 'just now';
    const d = at instanceof Date ? at : new Date(at);
    if (Number.isNaN(d.getTime())) return 'just now';
    const ageMs = Date.now() - d.getTime();
    if (ageMs < 2 * 60 * 1000) return 'just now';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).replace(',', ' at');
  };

  const hasUnsavedQuoteWork = () => {
    if (results && !results.isHistorical) return true;
    if (selectedProgramId && programResults && programInputsDirty) return true;
    return false;
  };

  const clearWorkspaceSelection = () => {
    setProjectNumber('');
    setSelectedProgramId('');
    setClientName('');
    setProjectVersion(1);
    setSelectedVersion(null);
    setSelectedProjectData(null);
    setHistoryDetailsOpen(false);
    setHistoryResults(null);
    setResults(null);
    setProgramResults(null);
    setWorkingQuoteReady(false);
    setProposalRefreshedAt(null);
    setProposalDriftWhileDrafting(false);
    setLoadedProposalFingerprint('');
    setPriorInvoiceTotal(null);
    setPriorProductTotal(null);
    autoRefreshedProjectRef.current = '';
    setInvoiceTotalManuallySet(false);
    setDepartureDateManuallySet(false);
    setReturnDateManuallySet(false);
    setInvoiceTotal('');
    setProductTotal('');
    setDepartureDate('');
    setReturnDate('');
    setEventDaysInput('1');
    setDeliveryAddress('');
    setError('');
    setSaveSuccess(false);
  };

  const handleSelectAnotherProjectOrProgram = () => {
    if (hasUnsavedQuoteWork()) {
      const ok = window.confirm(
        'You have an unsaved delivery quote in progress. Leave this workspace and discard it?'
      );
      if (!ok) return;
    }
    clearWorkspaceSelection();
  };

  const workspaceContextTitle = selectedProgram
    ? (selectedProgram.clientName || selectedProgram.programName || 'Program')
    : (selectedProjectData?.clientName || clientName || (projectNumber ? `Project ${projectNumber}` : ''));

  const workspaceContextMeta = selectedProgram
    ? [
        selectedProgram.programName && selectedProgram.programName !== selectedProgram.clientName
          ? selectedProgram.programName
          : '',
        formatDateRangeShort(selectedProgram.rangeStart, selectedProgram.rangeEnd),
        `${selectedProgram.events?.length || 0} Event${(selectedProgram.events?.length || 0) !== 1 ? 's' : ''}`,
      ].filter(Boolean).join(' · ')
    : [
        selectedProjectData?.venueName || '',
        formatContextEventRange(
          selectedProjectData?.startDate,
          selectedProjectData?.endDate,
          parseToLocalDate
        ),
      ].filter(Boolean).join(' · ');

  return (
    <div className="calculator-shell">
      {proposalsApiUrl && (
        <div className={`calc-section calc-section--selection${hasSelection ? ' calc-section--workspace' : ''}`}>
          {!hasSelection ? (
          <>
          {/* Entry state: choose Individual Project vs Program */}
          <div className="calc-tabs" role="tablist" aria-label="Calculation type">
            <button
              type="button"
              role="tab"
              aria-selected={calcMode === 'project'}
              className={`calc-tab ${calcMode === 'project' ? 'active' : ''}`}
              onClick={() => {
                setCalcMode('project');
                // program and single-project selection are mutually exclusive
                setSelectedProgramId('');
              }}
            >
              Individual Project
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={calcMode === 'program'}
              className={`calc-tab ${calcMode === 'program' ? 'active' : ''}`}
              onClick={() => {
                setCalcMode('program');
                // clear any single-project selection when switching to program mode
                setProjectNumber('');
                setSelectedProjectData(null);
                setResults(null);
              }}
            >
              Program
            </button>
          </div>

          {calcMode === 'project' ? (
            <>
              <label className="form-label">Project</label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <SearchableSelect
                    options={availableProjects.map(p => {
                      const dateRange = formatDateRange(p.startDate, p.endDate);
                      const venue = p.venueName || '';
                      const parts = [p.clientName, venue, dateRange].filter(Boolean);
                      return { value: p.projectNumber, label: parts.join(' - ') };
                    })}
                    value={projectNumber}
                    onChange={(value) => {
                      setProjectNumber(value);
                      setSelectedVersion(null);
                      setHistoryDetailsOpen(false);
                      setHistoryResults(null);
                      setResults(null);
                      setLoadedProposalFingerprint('');
                      setProposalDriftWhileDrafting(false);
                      setProposalRefreshedAt(null);
                      setWorkingQuoteReady(false);
                      setPriorInvoiceTotal(null);
                      setPriorProductTotal(null);
                      autoRefreshedProjectRef.current = '';
                      setDepartureDateManuallySet(false);
                      setReturnDateManuallySet(false);
                      if (value) {
                        setSelectedProgramId(''); // project and program selection are mutually exclusive
                        const selected = availableProjects.find(p => p.projectNumber === value);
                        if (selected) {
                          setClientName(selected.clientName);
                          setSelectedProjectData(selected);
                          // Seed working quote from latest proposal (user can Refresh again anytime).
                          setTimeout(() => {
                            applyProposalDataToWorkingQuote(selected, { preserveCalculatorInputs: false });
                            autoRefreshedProjectRef.current = String(value).trim();
                          }, 0);
                        }
                      } else {
                        setSelectedProjectData(null);
                        setInvoiceTotal('');
                        setProductTotal('');
                        setDepartureDate('');
                        setReturnDate('');
                        setEventDaysInput('1');
                        setDeliveryAddress('');
                      }
                    }}
                    placeholder={loadingProjects ? 'Loading projects…' : 'Search projects...'}
                    loading={loadingProjects}
                  />
                </div>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setProjectNumber('');
                    setSelectedProgramId('');
                    setClientName('');
                    setProjectVersion(1);
                    setSelectedVersion(null);
                    setSelectedProjectData(null);
                    setHistoryDetailsOpen(false);
                    setHistoryResults(null);
                    setResults(null);
                    setWorkingQuoteReady(false);
                    setProposalRefreshedAt(null);
                    autoRefreshedProjectRef.current = '';
                    setInvoiceTotalManuallySet(false);
                    setDepartureDateManuallySet(false);
                    setReturnDateManuallySet(false);
                    setInvoiceTotal('');
                    setProductTotal('');
                    setDepartureDate('');
                    setReturnDate('');
                    setEventDaysInput('1');
                    setDeliveryAddress('');
                  }}
                  style={{ padding: '14px 20px', fontSize: '12px', whiteSpace: 'nowrap' }}
                >
                  New Project
                </button>
              </div>
              {loadingProjects && (
                <div className="helper-text" style={{ marginTop: '8px' }}>
                  Loading projects…
                </div>
              )}
            </>
          ) : (
            <>
              <span className="helper-text" style={{ display: 'block', marginBottom: 12 }}>
                Calculate one delivery fee across multiple linked projects.
              </span>
              <label className="form-label">Program</label>
              <SearchableSelect
                options={programs.map((prog) => {
                  const dateRange = formatDateRangeShort(prog.rangeStart, prog.rangeEnd);
                  const parts = [
                    prog.clientName || prog.programName,
                    dateRange,
                    `${prog.events.length} Event${prog.events.length !== 1 ? 's' : ''}`,
                  ].filter(Boolean);
                  return { value: prog.programId, label: parts.join(' • ') };
                })}
                value={selectedProgramId}
                onChange={(value) => {
                  setSelectedProgramId(value || '');
                  if (value) {
                    // program and single-project selection are mutually exclusive
                    setProjectNumber('');
                    setSelectedProjectData(null);
                    setResults(null);
                  }
                }}
                placeholder={loadingProjects ? 'Loading programs…' : (programs.length ? 'Search programs...' : 'No programs available yet')}
                loading={loadingProjects}
              />
              {loadingProjects && (
                <div className="helper-text" style={{ marginTop: '8px' }}>
                  Loading programs…
                </div>
              )}
            </>
          )}
          </>
          ) : (
          <>
            <div className="calc-page-nav">
              <button
                type="button"
                className="calc-workspace-back"
                onClick={handleSelectAnotherProjectOrProgram}
              >
                <ChevronLeftIcon />
                <span>Select Another Project or Program</span>
              </button>
            </div>
            {(workspaceContextTitle || workspaceContextMeta) && (
              <div className="calc-workspace-context">
                {workspaceContextTitle ? (
                  <div className="calc-workspace-context__title">{workspaceContextTitle}</div>
                ) : null}
                {workspaceContextMeta ? (
                  <div className="calc-workspace-context__meta">{workspaceContextMeta}</div>
                ) : null}
              </div>
            )}
          </>
          )}

          {hasSelection && calcMode === 'program' && (
          <>
              {selectedProgram && programHistory.length > 0 && (() => {
                const latest = programHistory[0];
                const latestPd = latest.programData || {};
                const latestFee = typeof latest.totalFee === 'number'
                  ? latest.totalFee
                  : parseFloat(String(latest.totalFee || '0').replace(/[$,]/g, '')) || 0;
                return (
                  <div className="program-history">
                    <button
                      type="button"
                      className="program-history-toggle"
                      aria-expanded={programHistoryOpen}
                      onClick={() => setProgramHistoryOpen((o) => !o)}
                    >
                      <div className="program-history-summary">
                        <span className="program-history-title">Saved Program Deliveries ({programHistory.length})</span>
                        {!programHistoryOpen && (
                          <span className="program-history-meta">
                            Latest V{latest.version || 1} · {formatSavedDateLocal(latest.timestamp) || '—'} · {formatCurrency(latestFee)}
                          </span>
                        )}
                      </div>
                      <svg className={`accordion-chevron${programHistoryOpen ? ' open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    {programHistoryOpen && (
                      <table className="program-summary-table">
                        <thead>
                          <tr>
                            <th>Version</th>
                            <th>Saved</th>
                            <th className="num">Events</th>
                            <th>Market</th>
                            <th className="num">Total Fee</th>
                          </tr>
                        </thead>
                        <tbody>
                          {programHistory.map((c, idx) => {
                            const pd = c.programData || {};
                            const eventsCount = pd.includedCount != null
                              ? pd.includedCount
                              : (Array.isArray(pd.includedEvents) ? pd.includedEvents.length : null);
                            const fee = typeof c.totalFee === 'number'
                              ? c.totalFee
                              : parseFloat(String(c.totalFee || '0').replace(/[$,]/g, '')) || 0;
                            return (
                              <tr key={`${c.timestamp}-${idx}`} className={idx === 0 ? 'program-history-latest' : ''}>
                                <td>
                                  <span className="program-version-badge">V{c.version || 1}</span>
                                  {idx === 0 && <span className="latest-badge">Latest</span>}
                                </td>
                                <td data-label="Saved">{formatSavedDateLocal(c.timestamp) || '—'}</td>
                                <td className="num" data-label="Events">{eventsCount != null ? eventsCount : '—'}</td>
                                <td data-label="Market">{c.market || pd.market || '—'}</td>
                                <td className="num strong" data-label="Total Fee">{formatCurrency(fee)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })()}

              {selectedProgram && (
                <div className="program-summary">
                  <div className="program-summary-head">
                    <div className="program-summary-head-row">
                      <p className="program-summary-title">Included Events</p>
                      <span className="program-summary-count">
                        {includedEventProjects.length} of {selectedProgram.events.length} Events Included
                      </span>
                    </div>
                    <span className="helper-text" style={{ margin: 0 }}>
                      Uncheck any event to exclude it from this delivery calculation.
                    </span>
                  </div>
                  <table className="program-summary-table">
                    <thead>
                      <tr>
                        <th className="program-summary-checkbox" aria-label="Include"></th>
                        <th>Event</th>
                        <th>Proposal</th>
                        <th className="num">Invoice</th>
                        <th className="num">Product</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProgram.events.map((ev) => {
                        const inv = calculateInvoiceTotalFromProposal(ev);
                        const prod = calculateProductTotalFromProposal(ev);
                        const checked = includedEventProjects.includes(ev.projectNumber);
                        const dateRange = formatDateRangeShort(ev.startDate, ev.endDate);
                        const updated = getProposalUpdated(ev);
                        return (
                          <tr key={ev.projectNumber} className={checked ? '' : 'excluded'}>
                            <td className="program-summary-checkbox">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleIncludedEvent(ev.projectNumber)}
                                aria-label={`Include ${ev.venueName || ev.clientName || ev.projectNumber}`}
                              />
                            </td>
                            <td>
                              <span className="program-event-name">{ev.venueName || ev.clientName || ev.projectNumber}</span>
                              <span className="program-event-meta">
                                #{ev.projectNumber}{ev.status ? ` • ${ev.status}` : ''}
                              </span>
                              {dateRange && <span className="program-event-dates">{dateRange}</span>}
                            </td>
                            <td>
                              {/* Grouped so a future "stale proposal" flag can attach to this cell */}
                              <div className="proposal-cell">
                                <span className="program-version-badge">V{ev.version || 1}</span>
                                {updated && (
                                  <span className="proposal-updated">
                                    <span className="proposal-updated-label">Updated</span>
                                    <span className="proposal-updated-value">
                                      {updated.text}{updated.by ? ` by ${updated.by}` : ''}
                                    </span>
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="num" data-label="Invoice">{inv != null ? formatCurrency(inv) : '—'}</td>
                            <td className="num" data-label="Product">{prod != null ? formatCurrency(prod) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="program-totals">
                    <div className="program-totals-item">
                      <span className="program-totals-label">Invoice Total</span>
                      <span className="program-totals-value">{programItemization ? formatCurrency(programItemization.sumInvoice) : '—'}</span>
                    </div>
                    <div className="program-totals-item">
                      <span className="program-totals-label">Product Total</span>
                      <span className="program-totals-value">{programItemization ? formatCurrency(programItemization.sumProduct) : '—'}</span>
                    </div>
                  </div>
                </div>
              )}
          </>
          )}
        </div>
      )}

      {hasSelection && (
      <div className="calc-section calc-form" ref={detailsFormRef}>
        {!!projectNumberNorm && projectVersions.length > 0 && selectedSnapshot && (() => {
          const feeNum = roundMoney(selectedSnapshot.totalFee);
          const feeLabel = feeNum == null
            ? '—'
            : `$${feeNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          const snapInv = roundMoney(selectedSnapshot.invoiceTotal);
          const snapProd = roundMoney(selectedSnapshot.productTotal);
          return (
            <section className="calc-pp-section">
              <CalcSectionHead title="Current Delivery Quote" />
              <div className="calc-saved-quote">
                <p className="calc-saved-quote__summary">
                  <span className="calc-saved-quote__primary">
                    V{selectedSnapshot.version || selectedVersion}
                  </span>
                  <span className="calc-saved-quote__sep">·</span>
                  <span>Calculated {formatCalculatedDate(selectedSnapshot.timestamp) || '—'}</span>
                  <span className="calc-saved-quote__sep">·</span>
                  <span className="calc-saved-quote__fee">{feeLabel}</span>
                </p>
                <button
                  type="button"
                  className="calc-saved-quote__toggle"
                  onClick={() => setHistoryDetailsOpen((open) => !open)}
                  aria-expanded={historyDetailsOpen}
                >
                  <span>{historyDetailsOpen ? 'Hide Quote Details' : 'View Quote Details'}</span>
                  <ChevronDownIcon open={historyDetailsOpen} size={14} />
                </button>

                {historyDetailsOpen && (
                  <div className="calc-saved-quote__details">
                    <div className="calc-saved-quote__grid">
                      <div><span className="calc-saved-quote__field-label">Client</span>{selectedSnapshot.clientName || '—'}</div>
                      <div><span className="calc-saved-quote__field-label">Event Type</span>{selectedSnapshot.eventType || '—'}</div>
                      <div><span className="calc-saved-quote__field-label">Invoice Total</span>{snapInv != null ? formatCurrency(snapInv) : '—'}</div>
                      <div><span className="calc-saved-quote__field-label">Product Total</span>{snapProd != null ? formatCurrency(snapProd) : '—'}</div>
                      <div className="calc-saved-quote__span"><span className="calc-saved-quote__field-label">Delivery Address</span>{selectedSnapshot.deliveryAddress || '—'}</div>
                      <div><span className="calc-saved-quote__field-label">Departure</span>{formatDateDisplay(selectedSnapshot.departureDate) || '—'}</div>
                      <div><span className="calc-saved-quote__field-label">Return</span>{formatDateDisplay(selectedSnapshot.returnDate) || '—'}</div>
                      <div><span className="calc-saved-quote__field-label">Overnight</span>{selectedSnapshot.isOvernightRequired || '—'}</div>
                      <div><span className="calc-saved-quote__field-label">Nights</span>{selectedSnapshot.numberOfNights != null && selectedSnapshot.numberOfNights !== '' ? selectedSnapshot.numberOfNights : '—'}</div>
                      <div><span className="calc-saved-quote__field-label">Team Members</span>{selectedSnapshot.numberOfTeamMembers != null && selectedSnapshot.numberOfTeamMembers !== '' ? selectedSnapshot.numberOfTeamMembers : '—'}</div>
                      <div><span className="calc-saved-quote__field-label">Market</span>{selectedSnapshot.market || '—'}</div>
                    </div>
                    {historyResults && (
                      <p className="calc-saved-quote__result">
                        Delivery fee: <strong>{formatCurrency(historyResults.totalFee)}</strong>
                        {historyResults.standardFee === 'waived' ? ' (standard fee waived)' : ''}
                      </p>
                    )}
                  </div>
                )}

                {projectVersions.length > 1 && (
                  <div className="calc-quote-history">
                    <label className="form-label" htmlFor="calc-quote-history-select">Quote History</label>
                    <select
                      id="calc-quote-history-select"
                      className="select-field calc-history__select"
                      value={String(selectedVersion)}
                      onChange={(e) => handleSelectHistoryVersion(e.target.value)}
                    >
                      {projectVersions.map((calc, index) => {
                        const vFee = roundMoney(calc.totalFee);
                        const vFeeLabel = vFee == null
                          ? '0.00'
                          : vFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        const ver = parseInt(calc.version, 10) || 1;
                        return (
                          <option key={`${ver}-${index}`} value={String(ver)}>
                            {`V${ver} · ${formatCalculatedDate(calc.timestamp) || '—'} · $${vFeeLabel}`}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
              </div>
            </section>
          );
        })()}

        {!!projectNumberNorm && (
          <section className="calc-pp-section calc-pp-section--recalc" ref={newQuoteFormRef}>
            <CalcSectionHead title="Recalculate Delivery Quote" />
            <p className="calc-new-quote__support">
              Review the current proposal details and complete the delivery information below.
            </p>
            {workingQuoteReady && proposalDriftWhileDrafting ? (
              <div className="calc-proposal-drift" role="status">
                <span className="calc-refresh-status__text">
                  Proposal has changed since this data was refreshed
                </span>
                <span className="calc-refresh-status__sep"> · </span>
                <button
                  type="button"
                  className="calc-refresh-status__action"
                  onClick={() => handleRefreshProposalData({ preserveCalculatorInputs: true })}
                >
                  Refresh proposal data
                </button>
              </div>
            ) : (
              <p className="calc-refresh-status" role="status">
                {workingQuoteReady ? (
                  <>
                    <span className="calc-refresh-status__text">
                      Proposal data refreshed {formatProposalRefreshLabel(proposalRefreshedAt)}
                    </span>
                    <span className="calc-refresh-status__sep"> · </span>
                    <button
                      type="button"
                      className="calc-refresh-status__action"
                      onClick={() => handleRefreshProposalData({ preserveCalculatorInputs: true })}
                    >
                      Refresh
                    </button>
                  </>
                ) : (
                  <>
                    <span className="calc-refresh-status__text">Proposal data not loaded yet</span>
                    <span className="calc-refresh-status__sep"> · </span>
                    <button
                      type="button"
                      className="calc-refresh-status__action"
                      onClick={() => handleRefreshProposalData({ preserveCalculatorInputs: false })}
                    >
                      Refresh
                    </button>
                  </>
                )}
              </p>
            )}
          </section>
        )}

        <div className="calc-form-body">
        <div className="form-row">
          <div className="form-section form-section-half">
            <label className="form-label">
              Client Name {projectNumber ? '' : <span className="required">*</span>}
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="Enter client name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              disabled={!!projectNumber && !!availableProjects.find(p => p.projectNumber === projectNumber)}
            />
          </div>

          <div className="form-section form-section-half">
            <label className="form-label">
              Event Type <span className="required">*</span>
            </label>
            <select
              className="select-field"
              value={eventType}
              onChange={(e) => {
                setEventType(e.target.value);
                // Reset manual flags when switching event types
                setLodgingRoomsManuallySet(false);
                setLodgingNightsManuallySet(false);
                setLodgingTotalManuallySet(false);
              }}
            >
              <option value="Standard">Standard</option>
              <option value="Non-Standard Delivery">Non-Standard Delivery</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-section form-section-half">
            <label className="form-label">
              Invoice Total (post-discount, pre-tax, excl. delivery) <span className="required">*</span>
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="$0.00"
              inputMode="decimal"
              value={invoiceTotal !== '' ? formatCurrencyInput(invoiceTotal) : ''}
              onChange={(e) => {
                setInvoiceTotal(parseCurrencyInput(e.target.value));
                setInvoiceTotalManuallySet(true); // Mark as manually set when user types
              }}
              onBlur={(e) => setInvoiceTotal(normalizeCurrencyOnBlur(e.target.value))}
            />
            {workingQuoteReady &&
              priorInvoiceTotal != null &&
              invoiceTotal &&
              Math.abs(parseFloat(invoiceTotal) - priorInvoiceTotal) > 0.01 && (
                <div className="calc-field-notes">
                  <div className="calc-field-prior">
                    Previously {formatCurrency(priorInvoiceTotal)}
                  </div>
                </div>
              )}
          </div>

          <div className="form-section form-section-half">
            <label className="form-label">
              Product Total (post-discount) <span className="required">*</span>
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="$0.00"
              inputMode="decimal"
              value={productTotal !== '' ? formatCurrencyInput(productTotal) : ''}
              onChange={(e) => {
                setProductTotal(parseCurrencyInput(e.target.value));
                setProductTotalManuallySet(true); // Mark as manually set when user types
              }}
              onBlur={(e) => setProductTotal(normalizeCurrencyOnBlur(e.target.value))}
            />
            <div className="calc-field-notes">
              {workingQuoteReady &&
                priorProductTotal != null &&
                productTotal &&
                Math.abs(parseFloat(productTotal) - priorProductTotal) > 0.01 && (
                  <div className="calc-field-prior">
                    Previously {formatCurrency(priorProductTotal)}
                  </div>
                )}
              <div className="helper-text">
                {selectedProgramId
                  ? 'Combined from the included events above. Used for Complimentary Delivery Threshold calculations (single-day only).'
                  : 'Used for Complimentary Delivery Threshold calculations (single-day only).'}
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <label className="form-label">
            Delivery Address <span className="required">*</span>
          </label>
          <input
            ref={inputRef}
            type="text"
            className="input-field"
            placeholder="Start typing address..."
            value={deliveryAddress}
            onChange={(e) => {
              setDeliveryAddress(e.target.value);
              // Update timezone when address changes manually
              if (e.target.value) {
                const timezone = getTimezoneFromAddress(e.target.value);
                setDeliveryTimezone(timezone);
              }
            }}
          />
        </div>

        <div className="calc-date-block">
          <div className="form-row calc-date-fields">
            <div className="form-section form-section-half">
              <label className="form-label">
                Departure Date
              </label>
              <span className="helper-text">The date our team leaves the warehouse.</span>
              <input
                type="date"
                className="input-field"
                value={departureDate}
                onChange={(e) => {
                  setDepartureDate(e.target.value);
                  setDepartureDateManuallySet(true);
                }}
              />
            </div>

            <div className="form-section form-section-half">
              <label className="form-label">
                Return Date
              </label>
              <span className="helper-text">The date our team returns to the warehouse.</span>
              <input
                type="date"
                className="input-field"
                value={returnDate}
                onChange={(e) => {
                  setReturnDate(e.target.value);
                  setReturnDateManuallySet(true);
                }}
              />
            </div>
          </div>
          {selectedProjectData && selectedProjectData.startDate && (
            <p className="helper-text calc-date-guidance">
              Departure and return are team travel (warehouse leave → return). They drive overnight fees and Mayker/Non-Standard costs — not complimentary eligibility.
            </p>
          )}
        </div>

        {selectedProjectData && selectedProjectData.startDate && selectedProjectData.endDate ? (
          <div className="form-section">
            <label className="form-label">Event Days</label>
            <span className="helper-text">
              Inclusive load-in → strike from the proposal. Used only for complimentary delivery eligibility.
            </span>
            <div className="nights-input-container" style={{ opacity: 0.95 }}>
              <input
                type="number"
                className="input-field nights-input"
                value={(() => {
                  const n = calculateNightsFromDates(
                    selectedProjectData.startDate,
                    selectedProjectData.endDate
                  );
                  return n !== null ? Math.max(1, n + 1) : 1;
                })()}
                readOnly
                disabled
                min="1"
              />
              <span className="nights-label">
                event day
                {(() => {
                  const n = calculateNightsFromDates(
                    selectedProjectData.startDate,
                    selectedProjectData.endDate
                  );
                  const d = n !== null ? Math.max(1, n + 1) : 1;
                  return d !== 1 ? 's' : '';
                })()}
              </span>
            </div>
            <span className="helper-text" style={{ fontStyle: 'italic' }}>
              {formatDateRange(selectedProjectData.startDate, selectedProjectData.endDate)}
            </span>
          </div>
        ) : (
          <div className="form-section">
            <label className="form-label">
              Event Days <span className="required">*</span>
            </label>
            <span className="helper-text">
              Inclusive load-in → strike days. Controls complimentary eligibility. Separate from warehouse departure/return.
            </span>
            <div className="nights-input-container">
              <input
                type="number"
                className="input-field nights-input"
                placeholder="1"
                value={eventDaysInput}
                onChange={(e) => setEventDaysInput(e.target.value)}
                min="1"
              />
              <span className="nights-label">
                event day{parseInt(eventDaysInput, 10) !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        <div className="calc-pp-section calc-pp-section--travel">
          <CalcSectionHead title="Travel Details" />
        </div>

        <div className="form-row">
          <div className="form-section form-section-half">
            <label className="form-label">
              Is an overnight stay required? <span className="required">*</span>
            </label>
            <select
              className="select-field"
              value={isOvernightRequired}
              onChange={(e) => {
                setIsOvernightRequired(e.target.value);
                if (e.target.value === 'no') {
                  setNumberOfNights('');
                  setNightsManuallySet(false);
                } else {
                  setNightsManuallySet(false);
                }
              }}
            >
              <option value="">Select...</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="form-section form-section-half">
            <label className="form-label">
              Number of Team Members <span className="required">*</span>
            </label>
            <input
              type="number"
              className="input-field"
              placeholder="Enter number of team members"
              value={numberOfTeamMembers}
              onChange={(e) => setNumberOfTeamMembers(e.target.value)}
              min="1"
            />
          </div>
        </div>

        {isOvernightRequired === 'yes' && (
          <div className="form-section">
            <label className="form-label">
              How many nights? <span className="required">*</span>
              {departureDate && returnDate && !nightsManuallySet && (
                <span className="helper-text" style={{ display: 'inline', marginLeft: '8px', fontStyle: 'italic' }}>
                  (Auto-calculated from departure → return)
                </span>
              )}
            </label>
            <span className="helper-text">
              Team travel nights away — overnight fee only. Not used for complimentary eligibility.
            </span>
            <div className="nights-input-container">
              <input
                type="number"
                className="input-field nights-input"
                placeholder="0"
                value={numberOfNights}
                onChange={(e) => {
                  setNumberOfNights(e.target.value);
                  setNightsManuallySet(true);
                }}
                min="0"
              />
              <span className="nights-label">
                night{parseInt(numberOfNights) !== 1 ? 's' : ''}
              </span>
            </div>
            {departureDate && returnDate && nightsManuallySet && (
              <span className="helper-text" style={{ fontStyle: 'italic' }}>
                Manual override active. Clear the field to auto-calculate from departure → return.
              </span>
            )}
          </div>
        )}

        {eventType === 'Non-Standard Delivery' && (
          <>
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <label className="form-label">Lodging</label>
              <div className="form-row" style={{ marginBottom: '0' }}>
                <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
                  <input
                    type="number"
                    className="input-field"
                    placeholder="# of Rooms"
                    value={lodgingRooms}
                    onChange={(e) => {
                      setLodgingRooms(e.target.value);
                      setLodgingRoomsManuallySet(true);
                    }}
                    min="0"
                  />
                  <div className="helper-text" style={{ marginTop: '4px' }}>
                    # of Rooms: Auto-calculated from team members (÷ 2)
                  </div>
                </div>
                <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
                  <input
                    type="number"
                    className="input-field"
                    placeholder="# of Nights"
                    value={lodgingNights}
                    onChange={(e) => {
                      setLodgingNights(e.target.value);
                      setLodgingNightsManuallySet(true);
                    }}
                    min="0"
                  />
                  <div className="helper-text" style={{ marginTop: '4px' }}>
                    # of Nights: Auto-populated from # of nights above
                  </div>
                </div>
                <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="$0.00"
                    inputMode="decimal"
                    value={lodgingTotal !== '' ? formatCurrencyInput(lodgingTotal) : ''}
                    onChange={(e) => {
                      setLodgingTotal(parseCurrencyInput(e.target.value));
                      setLodgingTotalManuallySet(true);
                    }}
                    onBlur={(e) => {
                      setLodgingTotal(normalizeCurrencyOnBlur(e.target.value));
                      setLodgingTotalManuallySet(true);
                    }}
                  />
                  {lodgingTotalManuallySet ? (
                    <div className="helper-text" style={{ marginTop: '4px', fontStyle: 'italic' }}>
                      Manual override active. Change rooms/nights to auto-calculate.
                    </div>
                  ) : (
                    <div className="helper-text" style={{ marginTop: '4px' }}>
                      Total Lodging Cost: Auto-Calculated based on $250 per room per night. Edit as needed.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-section" style={{ marginBottom: '16px' }}>
              <label className="form-label">Gas</label>
              <input
                type="text"
                className="input-field"
                placeholder="$0.00"
                inputMode="decimal"
                value={gasTotal !== '' ? formatCurrencyInput(gasTotal) : ''}
                onChange={(e) => setGasTotal(parseCurrencyInput(e.target.value))}
                onBlur={(e) => setGasTotal(normalizeCurrencyOnBlur(e.target.value))}
              />
            </div>

            <div className="form-section" style={{ marginBottom: '16px' }}>
              <label className="form-label">Flights</label>
              <div className="form-row" style={{ marginBottom: '0' }}>
                <div className="form-section form-section-half" style={{ marginBottom: '0' }}>
                  <input
                    type="number"
                    className="input-field"
                    placeholder="# of Flights"
                    value={flightsCount}
                    onChange={(e) => setFlightsCount(e.target.value)}
                    min="0"
                  />
                </div>
                <div className="form-section form-section-half" style={{ marginBottom: '0' }}>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="$0.00"
                    inputMode="decimal"
                    value={flightsTotal !== '' ? formatCurrencyInput(flightsTotal) : ''}
                    onChange={(e) => setFlightsTotal(parseCurrencyInput(e.target.value))}
                    onBlur={(e) => setFlightsTotal(normalizeCurrencyOnBlur(e.target.value))}
                  />
                </div>
              </div>
            </div>

            <div className="form-section" style={{ marginBottom: '16px' }}>
              <label className="form-label">Per Diem / Day Rate</label>
              <div className="helper-text" style={{ marginBottom: '8px' }}>
                ${PER_DIEM_SUPERVISOR_RATE} for supervisors, ${PER_DIEM_TEAM_MEMBER_RATE} for delivery team members × number of days (nights + 1)
              </div>
              <div className="form-row" style={{ marginBottom: '0' }}>
                <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
                  <input
                    type="number"
                    className="input-field"
                    placeholder="# of Supervisors"
                    value={perDiemSupervisors}
                    onChange={(e) => {
                      setPerDiemSupervisors(e.target.value);
                      setPerDiemTotalManuallySet(false);
                    }}
                    min="0"
                  />
                </div>
                <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
                  <input
                    type="number"
                    className="input-field"
                    placeholder="# of Team Members"
                    value={perDiemTeamMembers}
                    onChange={(e) => {
                      setPerDiemTeamMembers(e.target.value);
                      setPerDiemTotalManuallySet(false);
                    }}
                    min="0"
                  />
                </div>
                <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="$0.00"
                    inputMode="decimal"
                    value={perDiemTotal !== '' ? formatCurrencyInput(perDiemTotal) : ''}
                    onChange={(e) => {
                      setPerDiemTotal(parseCurrencyInput(e.target.value));
                      setPerDiemTotalManuallySet(true);
                    }}
                    onBlur={(e) => {
                      setPerDiemTotal(normalizeCurrencyOnBlur(e.target.value));
                      setPerDiemTotalManuallySet(true);
                    }}
                  />
                  {perDiemTotalManuallySet ? (
                    <span className="helper-text" style={{ fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
                      Manual override active. Change supervisor/team member counts to auto-calculate.
                    </span>
                  ) : (
                    <div className="helper-text" style={{ marginTop: '4px' }}>
                      Auto-Calculates
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-section" style={{ marginBottom: '16px' }}>
              <label className="form-label">
                Do we plan to rent a truck for this project?
              </label>
              <select
                className="select-field"
                value={truckRentalRequired}
                onChange={(e) => {
                  setTruckRentalRequired(e.target.value);
                  if (e.target.value === 'no') {
                    setTruckRentalCount('');
                    setTruckRentalTotal('');
                  }
                }}
              >
                <option value="">Select...</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {truckRentalRequired === 'yes' && (
              <div className="form-section" style={{ marginBottom: '16px' }}>
                <label className="form-label">Truck Rental</label>
                <div className="form-row" style={{ marginBottom: '0' }}>
                  <div className="form-section form-section-half" style={{ marginBottom: '0' }}>
                    <input
                      type="number"
                      className="input-field"
                      placeholder="# of Trucks"
                      value={truckRentalCount}
                      onChange={(e) => setTruckRentalCount(e.target.value)}
                      min="1"
                    />
                  </div>
                  <div className="form-section form-section-half" style={{ marginBottom: '0' }}>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="$0.00"
                      inputMode="decimal"
                      value={truckRentalTotal !== '' ? formatCurrencyInput(truckRentalTotal) : ''}
                      onChange={(e) => setTruckRentalTotal(parseCurrencyInput(e.target.value))}
                      onBlur={(e) => setTruckRentalTotal(normalizeCurrencyOnBlur(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="form-section" style={{ marginBottom: '16px' }}>
              <label className="form-label">Other Expenses</label>
              {otherExpenses.map((expense, index) => (
                <div key={index} className="form-row" style={{ marginBottom: '12px', alignItems: 'flex-start' }}>
                  <div className="form-section" style={{ flex: 1, marginRight: '12px', marginBottom: '0' }}>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Expense name"
                      value={expense.name}
                      onChange={(e) => {
                        const updated = [...otherExpenses];
                        updated[index].name = e.target.value;
                        setOtherExpenses(updated);
                      }}
                    />
                  </div>
                  <div className="form-section" style={{ flex: '0 0 200px', marginRight: '12px', marginBottom: '0' }}>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="$0.00"
                      inputMode="decimal"
                      value={expense.total !== '' ? formatCurrencyInput(expense.total) : ''}
                      onChange={(e) => {
                        const updated = [...otherExpenses];
                        updated[index].total = parseCurrencyInput(e.target.value);
                        setOtherExpenses(updated);
                      }}
                      onBlur={(e) => {
                        const updated = [...otherExpenses];
                        updated[index].total = normalizeCurrencyOnBlur(e.target.value);
                        setOtherExpenses(updated);
                      }}
                    />
                  </div>
                  {otherExpenses.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        setOtherExpenses(otherExpenses.filter((_, i) => i !== index));
                      }}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: 500,
                        fontFamily: '"Neue Haas Unica", Inter, sans-serif',
                        backgroundColor: 'rgb(245, 244, 240)',
                        color: 'rgb(0, 0, 0)',
                        border: '1px solid rgb(232, 232, 227)',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: '0.2s',
                        boxShadow: 'rgba(0, 0, 0, 0.04) 0px 1px 3px'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setOtherExpenses([...otherExpenses, { name: '', total: '' }]);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  fontFamily: '"Neue Haas Unica", Inter, sans-serif',
                  backgroundColor: 'rgb(245, 244, 240)',
                  color: 'rgb(0, 0, 0)',
                  border: '1px solid rgb(232, 232, 227)',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: '0.2s',
                  boxShadow: 'rgba(0, 0, 0, 0.04) 0px 1px 3px',
                  marginTop: '8px'
                }}
              >
                Add Other Expense
              </button>
            </div>
          </>
        )}

        {error && <div className="error-message">{error}</div>}

        {!selectedProgramId && (
          <div className="calc-cta-block">
            <p className="calc-generate-helper">
              Calculate a new delivery fee using the information above.
            </p>
            <button
              className="btn-calculate"
              onClick={handleCalculate}
              disabled={
                loading ||
                (!!projectNumberNorm && !workingQuoteReady) ||
                !invoiceTotal ||
                !productTotal ||
                !eventType ||
                (eventType === 'Standard' && (!deliveryAddress || !isOvernightRequired || !numberOfTeamMembers || (isOvernightRequired === 'yes' && !numberOfNights))) ||
                (eventType === 'Non-Standard Delivery' && (!lodgingTotal && !gasTotal && !flightsTotal && !perDiemTotal && !truckRentalTotal && otherExpenses.every(e => !e.total)))
              }
            >
              {loading ? (
                <>
                  <span className="spinner-small"></span>
                  <span style={{ marginLeft: '8px' }}>Recalculating...</span>
                </>
              ) : (
                'Recalculate Delivery Quote'
              )}
            </button>
            {!workingQuoteReady && !!projectNumberNorm && (
              <p className="helper-text" style={{ marginTop: 8 }}>
                Refresh proposal data to populate this form before recalculating a quote.
              </p>
            )}
          </div>
        )}

        {saveSuccess && (
          <div className="calc-save-success" role="status">
            Delivery quote saved
            {selectedVersion != null ? ` as V${selectedVersion}` : ''}
          </div>
        )}

        </div>{/* end calc-form-body */}

        {selectedProgramId && (
          <>
            {programResults && programInputsDirty && (
              <div className="recalc-note">Inputs changed. Recalculate to update the delivery fee.</div>
            )}
            <button
              type="button"
              className="btn-calculate"
              onClick={handleCalculateProgram}
              disabled={
                programCalcLoading ||
                includedEventProjects.length === 0 ||
                !isOvernightRequired ||
                !numberOfTeamMembers ||
                (isOvernightRequired === 'yes' && !numberOfNights)
              }
            >
              {programCalcLoading
                ? 'Calculating…'
                : (programResults && programInputsDirty ? 'Recalculate Program Delivery' : 'Calculate Program Delivery')}
            </button>
            {programError && (
              <div className="error-message">{programError}</div>
            )}

            {programResults && (() => {
              const r = programResults;
              const isStretch = r.standardFee === null;
              const feeWaived = r.standardFee === 'waived';
              const standardDisplay = isStretch
                ? 'Contact JP (Stretch market)'
                : (feeWaived ? 'Waived (threshold met)' : formatCurrency(r.standardFee));
              return (
              <div className="program-results" ref={programResultsRef}>
                {r.marketDiscrepancy && (
                  <div className="result-warning">
                    Warning: selected events span different markets ({r.marketDiscrepancy.join(', ')}). Fee uses the {r.market} market — review before quoting.
                  </div>
                )}

                {/* 1 — Delivery fee hero: the outcome of the workflow */}
                <div className="result-hero" ref={programHeroRef}>
                  <div className="result-success">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Delivery Fee Calculated
                  </div>
                  <div className="result-hero-label">Program Delivery Fee</div>
                  <div className="result-hero-value">{isStretch ? 'Contact JP (Stretch market)' : formatCurrency(r.totalFee)}</div>
                  <div className="result-hero-lines">
                    <div className="result-hero-line">
                      <span className="result-hero-line-label">Standard Fee</span>
                      <span className="result-hero-line-value">{standardDisplay}</span>
                    </div>
                    {r.extendedFee > 0 && (
                      <div className="result-hero-line">
                        <span className="result-hero-line-label">Extended Timeline / Overnight</span>
                        <span className="result-hero-line-value">{formatCurrency(r.extendedFee)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* 2 — Recommended Delivery Allocation: how to bill the fee */}
                {r.allocations && r.allocations.length > 0 && (
                  <div className="result-block">
                    <h4 className="result-block-title">Recommended Fee Allocation</h4>
                    <span className="helper-text">How to bill this fee across events — distributed by {r.distributionBasis}.</span>
                    <table className="result-table">
                      <thead>
                        <tr>
                          <th>Event</th>
                          <th className="num">Product</th>
                          <th className="num">Share</th>
                          <th className="num">Delivery allocated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.allocations.map((a) => (
                          <tr key={a.projectNumber}>
                            <td>
                              <span className="result-event-name">{a.venueName || a.clientName || a.projectNumber}</span>
                              <span className="result-event-meta"> · #{a.projectNumber}</span>
                            </td>
                            <td className="num" data-label="Product">{formatCurrency(a.product || 0)}</td>
                            <td className="num" data-label="Share">{(a.share * 100).toFixed(1)}%</td>
                            <td className="num strong" data-label="Delivery allocated">{formatCurrency(a.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* 3 — Program Summary: the audit facts, shown once */}
                <div className="result-block">
                  <h4 className="result-block-title">Program Summary</h4>
                  <dl className="result-facts">
                    <div className="result-fact"><dt>Events</dt><dd>{r.includedCount}</dd></div>
                    <div className="result-fact"><dt>Market</dt><dd>{r.market}</dd></div>
                    <div className="result-fact"><dt>Invoice</dt><dd>{formatCurrency(r.sumInvoice)}</dd></div>
                    <div className="result-fact"><dt>Product</dt><dd>{formatCurrency(r.sumProduct)}</dd></div>
                  </dl>
                </div>

                {/* 4 — Calculation Details: collapsed by default, all detail preserved */}
                <div className="result-block">
                  <button
                    type="button"
                    className="accordion-toggle"
                    aria-expanded={programDetailsOpen}
                    onClick={() => setProgramDetailsOpen((o) => !o)}
                  >
                    <span>{programDetailsOpen ? 'Calculation Details' : 'Show Calculation Details'}</span>
                    <svg className={`accordion-chevron${programDetailsOpen ? ' open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {programDetailsOpen && (
                    <div className="accordion-body">
                      <div className="calc-summary-label">Calculation Summary</div>
                      <dl className="result-facts">
                        <div className="result-fact">
                          <dt>Complimentary Delivery Threshold</dt>
                          <dd>
                            {r.complimentaryBlockedByMultiDay
                              ? 'Does not apply to multi-day events'
                              : r.freeThresholdApplied != null
                                ? formatCurrency(r.freeThresholdApplied)
                                : '—'}
                            {!r.complimentaryBlockedByMultiDay && r.freeThresholdApplied != null && (
                              <span className="result-fact-note">
                                {feeWaived
                                  ? ' — met, standard fee waived'
                                  : `${r.amountToThreshold ? ` — ${formatCurrency(r.amountToThreshold)} more product would waive it` : ' — below threshold'}`}
                              </span>
                            )}
                          </dd>
                        </div>
                        <div className="result-fact">
                          <dt>Standard Rate</dt>
                          <dd>{isStretch ? 'Contact JP (Stretch market)' : `${formatPercent(r.feePercentage)} of invoice · minimum ${formatCurrency(r.minFee)}`}</dd>
                        </div>
                        <div className="result-fact">
                          <dt>Overnight Calculation</dt>
                          <dd>{r.extendedFee > 0 ? (r.extendedFeeExplanation || formatCurrency(r.extendedFee)) : 'None'}</dd>
                        </div>
                      </dl>

                      <div className="calc-summary-label" style={{ marginTop: 24 }}>Formula breakdown</div>
                      <ol className="program-logic-list">
                        <li>
                          <span className="assumption-label">Combine the selected events.</span>{' '}
                          {r.includedCount} deliveries → Combined Invoice {formatCurrency(r.sumInvoice)} · Combined Product {formatCurrency(r.sumProduct)}.
                        </li>
                        <li>
                          <span className="assumption-label">Determine the market.</span>{' '}
                          Driving distance to the general market places this program in the{' '}
                          <strong>{r.market}</strong> tier
                          {r.feePercentage != null && r.market !== 'Stretch' && (
                            <> (fee rate {formatPercent(r.feePercentage)} of invoice, minimum {formatCurrency(r.minFee)})</>
                          )}.
                          {r.marketDiscrepancy && (
                            <> Note: events span multiple tiers ({r.marketDiscrepancy.join(', ')}); the fee uses the {r.market} rate.</>
                          )}
                        </li>
                        {r.complimentaryBlockedByMultiDay ? (
                          <li>
                            <span className="assumption-label">Check the Complimentary Delivery Threshold.</span>{' '}
                            {MULTI_DAY_COMPLIMENTARY_NOTE} This program spans {r.thresholdDays} event day{r.thresholdDays !== 1 ? 's' : ''}, so a standard fee applies.
                          </li>
                        ) : r.freeThresholdApplied != null && (
                          <li>
                            <span className="assumption-label">Check the Complimentary Delivery Threshold.</span>{' '}
                            Threshold = {formatCurrency(r.freeThresholdApplied)} (standard single-day delivery).{' '}
                            {feeWaived
                              ? `Combined Product ${formatCurrency(r.sumProduct)} meets the threshold, so the standard fee is waived.`
                              : `Combined Product ${formatCurrency(r.sumProduct)} is below the threshold${r.amountToThreshold ? ` (${formatCurrency(r.amountToThreshold)} more product would waive it)` : ''}, so a standard fee applies.`}
                          </li>
                        )}
                        {typeof r.standardFee === 'number' && (
                          <li>
                            <span className="assumption-label">Calculate the standard fee.</span>{' '}
                            Greater of (Combined Invoice {formatCurrency(r.sumInvoice)} × {formatPercent(r.feePercentage)} = {formatCurrency(r.feeFromPercentage)}) or the {formatCurrency(r.minFee)} minimum ={' '}
                            <strong>{formatCurrency(r.standardFee)}</strong>
                            {r.feeBasis === 'minimum' ? ' (minimum applied).' : ' (percentage applied).'}
                          </li>
                        )}
                        {r.extendedFee > 0 && (
                          <li>
                            <span className="assumption-label">Add the overnight / extended fee (once for the program).</span>{' '}
                            {r.extendedFeeExplanation || formatCurrency(r.extendedFee)}.
                          </li>
                        )}
                        <li>
                          <span className="assumption-label">Total program delivery fee.</span>{' '}
                          {isStretch
                            ? 'Stretch market — contact JP for a custom quote.'
                            : `${feeWaived ? 'Standard fee waived' : formatCurrency(r.standardFee)}${r.extendedFee > 0 ? ` + ${formatCurrency(r.extendedFee)} extended` : ''} = ${formatCurrency(r.totalFee)}.`}
                        </li>
                      </ol>
                    </div>
                  )}
                </div>

                {/* Save — store this program delivery fee as versioned history */}
                {onSaveCalculation && (
                  <div className="program-save">
                    {programSaveInfo ? (
                      <div className="save-confirm-banner" role="status">
                        <span className="save-confirm-icon" aria-hidden="true">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                        <div className="save-confirm-text">
                          <span className="save-confirm-title">Program delivery saved</span>
                          <span className="save-confirm-sub">
                            Saved as V{programSaveInfo.version} · {formatSavedDateLocal(new Date(programSaveInfo.at).toISOString()) || 'just now'} · see Saved Program Deliveries above
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn-secondary save-confirm-again"
                          onClick={handleSaveProgram}
                          disabled={savingProgram}
                        >
                          {savingProgram ? 'Saving…' : 'Save Again'}
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn-calculate"
                          onClick={handleSaveProgram}
                          disabled={savingProgram || programInputsDirty}
                        >
                          {savingProgram ? 'Saving…' : 'Save Program Delivery'}
                        </button>
                        {programInputsDirty && (
                          <span className="helper-text" style={{ margin: 0 }}>
                            Recalculate before saving — inputs have changed.
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              );
            })()}

            {/* Compact sticky summary — keeps the answer visible while reviewing details */}
            {programResults && showProgramSticky && (
              <div className="program-sticky-bar" role="region" aria-label="Delivery fee summary">
                <div className="program-sticky-main">
                  <span className="program-sticky-label">Program Delivery Fee</span>
                  <span className="program-sticky-value">{programResults.standardFee === null ? 'Contact JP' : formatCurrency(programResults.totalFee)}</span>
                </div>
                <div className="program-sticky-lines">
                  <span>Standard: {programResults.standardFee === 'waived' ? 'Waived' : (programResults.standardFee === null ? '—' : formatCurrency(programResults.standardFee))}</span>
                  {programResults.extendedFee > 0 && (
                    <span>Extended: {formatCurrency(programResults.extendedFee)}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn-secondary program-sticky-btn"
                  onClick={handleCalculateProgram}
                  disabled={
                    programCalcLoading ||
                    includedEventProjects.length === 0 ||
                    !isOvernightRequired ||
                    !numberOfTeamMembers ||
                    (isOvernightRequired === 'yes' && !numberOfNights)
                  }
                >
                  {programCalcLoading ? 'Calculating…' : 'Recalculate'}
                </button>
              </div>
            )}
          </>
        )}

        {results && !results.isHistorical && (
          <div className="results-section" ref={resultsRef}>
            <div className="calc-new-quote-result">
              <div className="calc-new-quote-result__label">Recalculate Delivery Quote</div>
              <div className="calc-new-quote-result__amount">{formatCurrency(results.totalFee)}</div>
              <div className="calc-new-quote-result__meta">Calculated just now</div>
            </div>
            <div className="calc-pp-section">
              <CalcSectionHead title="Delivery Fee Breakdown" />
            </div>
            
            {results.isNonStandard ? (
              <div className="fee-breakdown">
                <div className="fee-row" style={{ borderBottom: 'none' }}>
                  <span className="fee-label">Estimated Delivery Cost (OOP)</span>
                  <span className="fee-value" style={{ fontSize: '20px', fontWeight: '600' }}>
                    {formatCurrency(results.totalFee)}
                  </span>
                </div>
                {results.manualBreakdown && (
                  <div style={{ marginTop: '8px', borderTop: '1px solid var(--brand-light-border)' }}>
                    <div style={{ fontSize: '14px', fontStyle: 'italic', marginTop: '16px', marginBottom: '8px', color: 'var(--brand-charcoal)', textAlign: 'center' }}>
                      Cost Breakdown
                    </div>
                    {results.manualBreakdown.lodging > 0 && (
                      <div className="fee-row" style={{ fontSize: '14px' }}>
                        <span className="fee-label">Lodging</span>
                        <span className="fee-value">{formatCurrency(results.manualBreakdown.lodging)}</span>
                      </div>
                    )}
                    {results.manualBreakdown.gas > 0 && (
                      <div className="fee-row" style={{ fontSize: '14px' }}>
                        <span className="fee-label">Gas</span>
                        <span className="fee-value">{formatCurrency(results.manualBreakdown.gas)}</span>
                      </div>
                    )}
                    {results.manualBreakdown.flights > 0 && (
                      <div className="fee-row" style={{ fontSize: '14px' }}>
                        <span className="fee-label">Flights</span>
                        <span className="fee-value">{formatCurrency(results.manualBreakdown.flights)}</span>
                      </div>
                    )}
                    {results.manualBreakdown.perDiem > 0 && (
                      <div className="fee-row" style={{ fontSize: '14px' }}>
                        <span className="fee-label">Per Diem / Day Rate</span>
                        <span className="fee-value">{formatCurrency(results.manualBreakdown.perDiem)}</span>
                      </div>
                    )}
                    {results.manualBreakdown.truckRental > 0 && (
                      <div className="fee-row" style={{ 
                        fontSize: '14px', 
                        borderBottom: (results.manualBreakdown.otherExpenses && Array.isArray(results.manualBreakdown.otherExpenses) && results.manualBreakdown.otherExpenses.length > 0) ? '1px solid var(--brand-light-border)' : 'none',
                        fontWeight: '400',
                        borderTop: 'none',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        marginTop: '0'
                      }}>
                        <span className="fee-label" style={{ fontSize: '14px', fontWeight: '400' }}>Truck Rental</span>
                        <span className="fee-value" style={{ fontSize: '14px', fontWeight: '400' }}>{formatCurrency(results.manualBreakdown.truckRental)}</span>
                      </div>
                    )}
                    {(() => {
                      const hasOtherExpenses = results.manualBreakdown.otherExpenses && Array.isArray(results.manualBreakdown.otherExpenses) && results.manualBreakdown.otherExpenses.length > 0;
                      console.log('Checking otherExpenses for rendering:', {
                        exists: !!results.manualBreakdown.otherExpenses,
                        isArray: Array.isArray(results.manualBreakdown.otherExpenses),
                        length: results.manualBreakdown.otherExpenses?.length,
                        data: results.manualBreakdown.otherExpenses,
                        hasOtherExpenses
                      });
                      return null;
                    })()}
                    {results.manualBreakdown.otherExpenses && Array.isArray(results.manualBreakdown.otherExpenses) && results.manualBreakdown.otherExpenses.length > 0 && (
                      <>
                        {results.manualBreakdown.otherExpenses.map((expense, idx) => {
                          const isLast = idx === results.manualBreakdown.otherExpenses.length - 1;
                          // Parse total - handle both formatted strings and number strings
                          let expenseTotal = 0;
                          if (expense.total) {
                            const totalStr = String(expense.total).replace(/[^\d.]/g, '');
                            expenseTotal = parseFloat(totalStr) || 0;
                          }
                          const expenseNameRaw = expense.name ? String(expense.name).trim() : '';
                          const expenseName = expenseNameRaw || 'Other Expense';
                          
                          console.log('Rendering other expense:', { idx, expenseName, expenseTotal, expense, expenseNameRaw, totalRaw: expense.total });
                          
                          return (
                            <div key={idx} className="fee-row" style={{ 
                              fontSize: '14px', 
                              borderBottom: isLast ? 'none' : '1px solid var(--brand-light-border)',
                              fontWeight: '400',
                              borderTop: 'none',
                              paddingTop: '12px',
                              paddingBottom: '12px',
                              marginTop: '0'
                            }}>
                              <span className="fee-label" style={{ fontSize: '14px', fontWeight: '400' }}>{expenseName}</span>
                              <span className="fee-value" style={{ fontSize: '14px', fontWeight: '400' }}>{formatCurrency(expenseTotal)}</span>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="fee-breakdown">
                <div className="fee-row">
                  <span className="fee-label">Standard Delivery Fee</span>
                  <span className={`fee-value ${results.standardFee === 'waived' ? 'waived-text' : ''}`}>
                    {results.standardFee === 'waived' 
                      ? 'Waived due to Complimentary Delivery Threshold'
                      : results.standardFee === null
                      ? 'Case-by-case (contact team)'
                      : formatCurrency(results.standardFee)}
                  </span>
                </div>
                
                {results.extendedFee > 0 && (
                  <div className="fee-row">
                    <span className="fee-label">Extended Delivery Fee</span>
                    <span className="fee-value">{formatCurrency(results.extendedFee)}</span>
                  </div>
                )}
                
                <div className="fee-row">
                  <span className="fee-label">Total Delivery Fee</span>
                  <span className="fee-value">
                    {results.standardFee === null 
                      ? 'Case-by-case (contact team)'
                      : formatCurrency(results.totalFee)}
                  </span>
                </div>
              </div>
            )}

            <div className="assumptions-section">
              <div className="calc-pp-section">
                <CalcSectionHead title="Assumptions" />
              </div>
              <ul className="assumptions-list">
                {results.market && (
                  <li>
                    <span className="assumption-label">Market:</span> {results.market}
                  </li>
                )}
                {results.distance !== null && results.distance !== undefined && (
                  <li>
                    <span className="assumption-label">Miles from warehouse:</span> {results.distance} mi
                  </li>
                )}
                {(results.eventDays != null || results.thresholdDays != null) && (
                  <li>
                    <span className="assumption-label">Event days (load-in → strike):</span>{' '}
                    {results.eventDays != null ? results.eventDays : results.thresholdDays}
                    {results.eventDaysSource === 'manual' ? ' (entered)' : ''}
                  </li>
                )}
                {results.days !== null && results.days !== undefined && (
                  <li>
                    <span className="assumption-label">Team travel days (warehouse leave → return):</span> {results.days}
                  </li>
                )}
                {results.nights !== null && results.nights !== undefined && (
                  <li>
                    <span className="assumption-label">Team nights away:</span> {results.nights}
                  </li>
                )}
                {results.teamMembers !== null && results.teamMembers !== undefined && (
                  <li>
                    <span className="assumption-label"># of team members:</span> {results.teamMembers}
                  </li>
                )}
                {results.complimentaryBlockedByMultiDay && (
                  <li>
                    <span className="assumption-label">Complimentary Delivery Threshold:</span> {MULTI_DAY_COMPLIMENTARY_NOTE}
                  </li>
                )}
                {!results.complimentaryBlockedByMultiDay && results.freeThresholdApplied != null && (
                  <li>
                    <span className="assumption-label">Complimentary Delivery Threshold:</span> {formatCurrency(results.freeThresholdApplied)}
                  </li>
                )}
                {results.amountToThreshold !== null && results.amountToThreshold !== undefined && (
                  <li>
                    <span className="assumption-label">Amount to Complimentary Delivery Threshold:</span> {results.amountToThreshold === 0 ? 'Threshold Achieved' : formatCurrency(results.amountToThreshold)}
                  </li>
                )}
                {results.extendedFeeExplanation && (
                  <li>
                    <span className="assumption-label">Calculation Context for Per Diem/Day Rate:</span> {results.extendedFeeExplanation}
                  </li>
                )}
              </ul>
            </div>

            {onSaveCalculation && (
              <>
                <div style={{ marginTop: '24px', marginBottom: '16px', fontSize: '14px', color: 'var(--brand-text-dark)', lineHeight: '1.6', textAlign: 'center' }}>
                  This quote is not saved yet. Saving adds it to Quote History as the next permanent record. Prior quotes remain unchanged.
                </div>
                <button
                  className="btn-calculate"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : `Save as V${projectVersion}`}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

export default Calculator;

